'use client';

// Глобальный drawer кабинета с iOS-шапкой: слева ×, центр заголовок, справа ✓ (опц.).
// Любая страница вызывает useDrawer().open({...}); содержимое — произвольный JSX.
import { createContext, startTransition, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { flushSync } from 'react-dom';
import { nativeBridge } from '@/lib/native-app';
import { sheetTopFromDetent } from '@/lib/sheet-detent';
import { closeStep, levelDrag, liveLevels, outsideTap } from '@/lib/drawer-levels';
import { PhoneLookCtx, setFramesOpen } from './layout-mode';
import { useTranslations } from 'next-intl';
import { KBareButton } from '@scriptera/ui/kit';
import { takenByFolders } from './folder-pager';

export interface DrawerPayload {
  title: string;
  body: React.ReactNode;
  /** ВОПРОС ПЕРЕД ЗАКРЫТИЕМ РУКОЙ (крестик шапки, смахивание листа): «Отменить создание?»
   *  у мастера на создании (ТЗ WEB-PARITY-3 6.0 п.2, как в iOS). Обещание «можно
   *  закрывать»; false — окно остаётся. Программное `close()`/`closeAll()` (бот создан,
   *  уход на страницу) вопрос не задаёт; «назад» браузера не перехватывается. */
  closeAsk?: () => Promise<boolean>;
  /** ЛИСТ БЕЗ ШАПКИ на телефоне (кадр iOS B05-plus-templates: крупный заголовок в теле,
   *  крестика нет — закрытие смахиванием/тапом мимо). На широком экране шапка остаётся. */
  headless?: boolean;
  /** АДРЕС ЭТОГО УРОВНЯ (ТЗ WEB-LOOK Э0.4, Закон 2, владелец 30.09.2026: «шторки не пишут
   *  адрес — это неправильно»). Задан только у экранов-страниц (мастер бота, окно бота,
   *  переписка, подстраница «Аккаунта», новая рассылка…): открытие кладёт запись истории
   *  `history.pushState`, «назад» браузера / «‹» / Escape / клик мимо снимают уровень одним
   *  путём через popstate, а загрузка страницы по этому адресу открывает тот же уровень
   *  (это делает раздел-хозяин, не drawer). Без `href` уровень живёт как раньше — 136
   *  прежних вызовов не меняются ни на байт. Адреса кабинета — без языка (lib/cabinet-nav.ts). */
  href?: string;
  /** Куда вернуть адрес, если уровень с `href` открыли по прямому заходу (записи истории
   *  назад нет): на закрытии — `history.replaceState(closeHref)`. */
  closeHref?: string;
  actionLabel?: string;
  /** ✓ в шапке. Drawer НЕ закрывается сам: обработчик (напр. async-сохранение)
   * закрывает через useDrawer().close() после успеха. */
  onAction?: () => void;
  /** SF-иконка кнопки-действия для НАТИВНОГО верха шторки в приложении (напр. info.circle);
   *  веб-drawer её игнорирует (правый слот в вебе — headerRight/✓ по actionLabel). */
  actionIcon?: string;
  /** ВТОРАЯ правая кнопка НАТИВНОЙ шапки слоя (фильтр рядом с «+», владелец 2026-08-07).
   *  Веб-drawer игнорирует: на сайте фильтр остаётся кружком в теле раздела. */
  action2Icon?: string;
  action2Label?: string;
  onAction2?: () => void;
  /** Родной поиск шторки в приложении (лупа в нативной шапке, ввод — searchQuery);
   *  веб-drawer игнорирует — на вебе поиск рисует само содержимое (видимое поле). */
  search?: { placeholder?: string };
  onSearch?: (q: string) => void;
  /** Широкий drawer (для админ-разделов с таблицами). */
  wide?: boolean;
  /** Почти на весь экран (95vw) — для «Логов» админки (владелец 2026-07-17). */
  full?: boolean;
  /** Средний drawer — по максимальной ширине формы модели (шторка «Модели», этап 7). */
  md?: boolean;
  /** Вложенный экран: вместо крестика — стрелка «назад», клик НЕ закрывает drawer. */
  onBack?: () => void;
  /** ГОРИЗОНТАЛЬНЫЙ слой во всю высоту, выезжающий СПРАВА — на телефоне и в приложении
   *  одинаково. Что оно включает в вебе: свайп-назад слева направо вместо свайпа-вниз и
   *  участие в СТЕКЕ УРОВНЕЙ (см. winKey ниже). На широком экране окно — та же плавающая
   *  панель, что у всех модалок кабинета, и уровни едут тем же выездом. В приложении
   *  направление решает отсутствие `sheet`: боковой слой рисует натив.
   *  ОКНА КОНСТРУКТОРА СНОВА ЖИВУТ ИМЕННО ЭТИМ (владелец 25.08.2026, вечер — набор
   *  EDITOR_SIDE): правка блока это шаг ВГЛУБЬ внутри схемы, а не новый контекст, поэтому
   *  она едет справа в пределах той же карты; утренняя вертикаль того же дня отменена, а
   *  вертикальным листом остались только новый уровень схемы (шторка ленты) и окна страницы
   *  сценария. Открытое ИЗ ЛИСТА боковое окно ложится СТОПКОЙ поверх живого листа (см.
   *  winKey ниже), в приложении — вложенным слоем в раме листа. Флагом живут и ДРУГИЕ
   *  шторки кабинета — пикеры с поиском, широкие таблицы, база знаний, редакторы (канон
   *  NATIVE-SLIDE-PLAN §11 п.10). */
  side?: boolean;
  /** Имя УРОВНЯ окна (с `sheet` — вертикальные листы, ими с 25.08.2026 живут ВСЕ окна
   *  конструктора и шторка ленты; с `side` — горизонтальные окна): «catalog», «node», «picker»…
   *  По нему drawer ведёт единый стек уровней — на любом экране (владелец 13.08.2026,
   *  десктоп добавлен 18.08.2026):
   *   • имени в стеке ещё нет → открытие кладёт НОВЫЙ уровень поверх живого нижнего
   *     (каталог остаётся под настройкой, «←» возвращает в него);
   *   • имя уже в стеке → всё, что выше, снимается, и сам уровень обновляется на месте
   *     (так настройки шага возвращаются после пикера/подключения — без дублей).
   *  Без winKey уровень тоже кладётся стопкой (с 05.10.2026, ТЗ «Причины» П1): имя нужно
   *  только для возврата к уровню и правки на месте; подмена — лишь по флагу `replace`.
   *
   *  СТЕК РАБОТАЕТ И У ВЕРТИКАЛЬНЫХ ЛИСТОВ (владелец 25.08.2026, шторка ленты). Раньше он
   *  был заведён только для горизонтальных окон конструктора, потому что других вложенных
   *  окон не было. Шторка ленты («шаг с несколькими входами живёт в шторке») открывается
   *  ВЕРТИКАЛЬНЫМ листом и внутри себя открывает следующую такую же — значит стопка нужна
   *  и там. Механика ОДНА на оба направления: имя уровня решает, класть новый слой или
   *  срезать стек до существующего; разъезжается только картинка (лист уезжает вниз,
   *  окно — вправо).
   *  ЧТО НА ЧТО ЛОЖИТСЯ (владелец 25.08.2026, вечер): лист на лист, окно на окно И ОКНО НА
   *  ЛИСТ (правка блока из шторки ленты — шаг вглубь в пределах той же карты). Лист поверх
   *  окна стопкой не кладётся: это новый контекст. */
  winKey?: string;
  /** ПОДМЕНА НА МЕСТЕ — ТОЛЬКО ПО ЯВНОМУ ФЛАГУ (ТЗ «Причины» П1, 05.10.2026; претензии М21/М23,
   *  Д2.2 п.13: «Настройки → Подключения без хода, „‹“ уводит в Аккаунт», «Прошёл бота подменой»).
   *  По умолчанию открытие при живом окне кладёт НОВЫЙ уровень стопки — как родной push iOS.
   *  `replace: true` ставит тот, кто открывает ТОТ ЖЕ экран заново: смена режима корня (вход ↔
   *  регистрация), шапка «Создать бота» по навигации внутри тела (onNav), «Информация» модели
   *  ИИ тем же телом. Верхний уровень меняет содержимое на месте, без хода. Тот же самый `body`
   *  (одна ссылка на элемент) или тот же `href`, что у верхнего уровня, считаются тем же
   *  экраном и подменяются без флага. */
  // INVARIANT(scriptera): БЕЗ `replace` ОТКРЫТИЕ ПРИ ЖИВОМ ОКНЕ — НОВЫЙ УРОВЕНЬ, А НЕ ПОДМЕНА.
  // Причина: подмена без хода и «‹», снимающая всё окно (М21/М23/Д2.2 п.13, след 05.10 P2 шаг 13).
  // Канон: docs/NATIVE-SLIDE-PLAN.md §11 п.8 «вложенность = стопка слоёв, не подмена».
  replace?: boolean;
  /** iOS-шторка на мобиле (владелец 2026-07-18): выезжает снизу НЕ до верха (зазор,
   * скруглённые верхние углы). Без флага мобильная модалка полноэкранна и выезжает
   * справа (как десктоп). На десктопе флаг не меняет ничего: правила листа живут в
   * мобильном медиазапросе (владелец 2026-07-31: широкий экран остаётся прежним).
   * Поля ввода листу больше НЕ противопоказаны: клавиатуру ведёт фокус-прокси у верха
   * листа (useSheetKeyboardProxy ниже, канон IOS-KEYBOARD-CANON §6) — прежняя оговорка
   * «только для модалок без полей» отпала вместе с ним. */
  sheet?: boolean;
  /** Лист открыт НЕ ДО ВЕРХА: доля экрана снизу (0..1) — 0.4 у подтверждений и прочих
   *  коротких листов кабинета (владелец 2026-07-21). У ОКОН СЦЕНАРИЯ И КОНСТРУКТОРА
   *  детента НЕТ (владелец 25.08.2026: «открывать повыше, почти до островка») — прежние
   *  0.88 (К35) сняты, лист без детента встаёт сразу под вырез.
   *  В приложении — родной детент слоя; в вебе — верхняя кромка листа (--sheet-top). */
  sheetDetent?: number;
  /** ЛИСТ-СТОПКА (Н39, владелец 02.09.2026; словарь — NATIVE-SLIDE-PLAN §11.0): тот же
   *  нижний лист, но В ПРИЛОЖЕНИИ экран под ним уходит назад колодой, а за ним встаёт
   *  фирменная подложка (§11 п.18). Так открывается КОНЕЧНЫЙ ОБЪЕКТ из списка (карточка
   *  доступа, человека, товара, сотрудника) — «объект из любого списка — стопка».
   *  Только вместе с `sheet`; читает его ТОЛЬКО родной слой (native-layer-host → push).
   *  В БРАУЗЕРЕ ФЛАГ НЕ ЧИТАЕТСЯ ВОВСЕ (закон владельца 07.10.2026, ТЗ WEB-CAUSES М87/М90/М92):
   *  под ЛЮБЫМ вертикальным листом экран уходит назад колодой, лист несёт ручку, тень по
   *  периметру и плёнку на весь экран — один закон хоста, у вызовов флагов вида нет. Прежние
   *  `deck: false` (М82) и ветки «лист без колоды» сняты. */
  deck?: boolean;
  /** ЛЕВЫЙ КРУЖОК ШАПКИ: «×» или «‹» (ТЗ WEB-CAUSES М83, доработка ФИНАЛ-2 06.10.2026; закон iOS):
   *  «‹» — страница, протолкнутая ГОРИЗОНТАЛЬНО внутри того же листа (карточка подключения K12,
   *  страница папки C04, аналитика рассылки R07, подстраницы «Аккаунта»); «×» — НОВЫЙ лист,
   *  представленный поверх ВЕРТИКАЛЬНО, даже если он не корень стопки (мастер шаблона W11, «Новая
   *  рассылка» R02, листы правки окна бота B09–B12). Флаг `sheet` этого не различает (страница папки
   *  тоже лист), поэтому вид задаёт открыватель. Не задан — прежнее: вложенный → «‹», корень → «×».
   *  Поведение у вложенного уровня одно при обоих видах — снимается ЭТОТ уровень, окно не закрывается. */
  lead?: 'close' | 'back';
  /** СВОЁ ДЕЙСТВИЕ КРУЖКА ШАПКИ (и смахивания листа) вместо штатного «снять этот уровень» — для
   *  вложенного уровня, который закрывает БОЛЬШЕ себя: экран результата оплаты стоит над листом
   *  кассы, подтверждением и расчётом, и его «×» снимает весь путь оплаты (`closeFrom`), а не
   *  возвращает к «Перейти к оплате» (ROBOKASSA-SHEET-TZ §3.1, владелец 10.10.2026). Проходит через
   *  `closeAsk`, как и штатные действия. */
  onLead?: () => void;
  /** СТРАНИЦА ВНУТРИ ЛИСТА (владелец 09.10.2026, центр уведомлений на телефоне в браузере): вложенный
   *  уровень вместе с `sheet` встаёт в раму листа под ним (тот же верх, те же углы) и едет СПРАВА
   *  НАЛЕВО в его пределах, как родной push внутри шторки iOS; лист под ним остаётся на месте и
   *  темнеет (§11 п.19), снимается свайпом вправо и «‹». Только телефон в браузере: ставит
   *  открыватель за признаком телефона, вид — cabinet.css (`.dr-insheet`). У корня не читается. */
  inSheet?: boolean;
  /** КРУПНЫЙ ЗАГОЛОВОК СТРАНИЦЫ-СПИСКА (APP-CLAIMS-1020 ред. 11, Д10 п.10.6 (Р20/С2); страница сервера
   *  `chrome.largeTitle`, у 2.0 — только список «Рассылки»): слово стоит крупно (34/700) первой строкой тела, как
   *  largeTitle навигации iOS, а в шапке проявляется по мере прокрутки (--dr-p, 24 пункта). Только телефон в
   *  браузере — ставит открыватель. У «Контактов» его нет (С2). */
  largeTitle?: boolean;
  /** Произвольный элемент в правом слоте шапки (напр. кнопка «Фильтры»); заменяет ✓. */
  headerRight?: React.ReactNode;
  /**
   * Произвольный элемент в ЛЕВОМ слоте шапки — заменяет ведущую кнопку (× или ‹).
   * Заведён экраном «Кто получит» (MESSAGE-COMPOSER И9.1, владелец 04.09.2026, образец
   * «Почта» iOS): в режиме галочек слева стоит СЛОВО «Выбрать всё» / «Снять всё», а не
   * кружок «назад». Больше нигде его не ставить — общая шапка кабинета остаётся прежней
   * («слева крестик, справа галочка», владелец 11.08.2026); собственное закрытие слоя
   * такой слот обязан делать сам.
   */
  headerLeft?: React.ReactNode;
  /** РОДНОЕ меню кнопки-действия НАТИВНОГО верха (UIMenu, iOS-«селект») — только для
   *  приложения (native-layer-host); веб-drawer рисует свой headerRight, меню игнорирует.
   *  sub — серая подпись под пунктом (subtitle UIAction; напр. адрес почты). */
  actionMenu?: Array<{ key: string; label: string; icon?: string; sub?: string }>;
  onMenu?: (key: string) => void;
  /** ✓ ШАПКИ ПОГАШЕН (веб; кадры iOS M20/M24/M25 — у кнопки без подписи или с негодным
   *  адресом галочка серая и не нажимается). Ставит хозяин тела через `update`. */
  actionDisabled?: boolean;
  /** Верх шторки ТЁМНЫЙ (герой-градиент подстраниц «Настроек») — в приложении стекло кнопок
   *  нативной шапки стартует тёмным, без мигания «тёмное→светлое»; веб-drawer игнорирует.
   *  Не задан — светлый верх: стекло по теме кабинета (владелец 2026-07-26). */
  glassDark?: boolean;
  /** ОКНО КОНСТРУКТОРА: пометка «это окно схемы», НЕ размер и НЕ место.
   *  ГЕОМЕТРИЯ И ЖЕСТ У ВСЕХ ОКОН ОДНИ (ЖЕЛЕЗНОЕ, владелец 18.08.2026, Г1): каталог,
   *  настройка условия, пикер блоков и редактор текста — та же полноразмерная шторка,
   *  что у модалок остальных страниц кабинета, и тот же выезд справа налево. Прежние
   *  размещения ЧП («panel» — узкая карточка 392, «modal» — центральная модалка с
   *  проявлением, «cpFit» — коробочка по содержимому у кнопки) ОТМЕНЕНЫ: окна «возникали
   *  на месте» и были разного размера. Поле оставлено ради оформления СОДЕРЖИМОГО
   *  (плоские списки внутри окон конструктора, cabinet.css) — геометрии оно не трогает.
   *  В ПРИЛОЖЕНИИ не влияет ни на что: там слои и их шапку рисует натив. */
  cp?: boolean;
  /** Значок блока перед заголовком — веб-drawer его НЕ рисует (шапка единая, заголовок
   *  текстом); поле живёт ради нативного верха приложения (native-layer-host). */
  titleIcon?: React.ReactNode;
  /** Подзаголовок: серая строка под названием (счётчик «Шаг N из M» мастера,
   *  владелец 2026-08-05). */
  subtitle?: string;
  /** ПОДЗАГОЛОВОК ПОД ЗАГОЛОВКОМ В СТРОКЕ КРУЖКОВ (телефон, браузер; окно «Сообщение»: «Сообщение /
   *  Рассылка», кадр iOS M01): обе строки по центру шапки, крупного заголовка в теле нет. */
  subStack?: boolean;
  /** КОЛЬЦО-ПРОГРЕСС ПЕРЕД ПОДЗАГОЛОВКОМ (ТЗ WEB-PARITY-3 М3, кадр iOS W11/W32: розовое
   *  кольцо слева от «Шаг N из M»). Доля 0…1 пройденного; нет — кольца нет. */
  progress?: number;
  /** Корзина в шапке: удалить шаг/блок. Нет — корзины нет. */
  onTrash?: () => void;
  trashLabel?: string;
}

interface DrawerApi {
  open: (payload: DrawerPayload) => void;
  /** Закрыть ОКНО ЦЕЛИКОМ (все уровни стека). */
  close: () => void;
  /** Закрыть ОДИН ВЕРХНИЙ УРОВЕНЬ; уровень один — закрывается окно (ТЗ WEB-PARITY-3, К3.1,
   *  владелец 02.10.2026). Этим ходит «закрыть» поверхностей (`useSlidePanel().close`) —
   *  ровно как в iOS-приложении, где «закрыть» снимает один верхний слой. От `back`
   *  отличается корнем: у корневого уровня это всегда закрытие окна, а не его `onBack`.
   *  Необязательный по той же причине, что `back`: типом ходят и обёртки приложения. */
  closeLevel?: () => void;
  /** СНЯТЬ ПОТОК УРОВНЕЙ (05.10.2026, ТЗ «Причины» П1): нижний живой уровень с одним из имён
   *  `winKeys` и всё, что над ним. Нужен потоку из нескольких листов одного дела — меню видов
   *  триггера → список ботов → страница триггера мастера: «Готово» на верхнем снимает весь
   *  поток разом, как `wizFlowClose` родной шторки потока (WizardScreen.swift). Имени в стеке
   *  нет — снимается один верхний уровень (как `closeLevel`). */
  closeFrom?: (winKeys: readonly string[], opts?: { lowerKeepDeck?: boolean }) => void;
  /** Назад НА УРОВЕНЬ ВЫШЕ (владелец 13.08.2026): есть уровень под текущим — снимаем
   *  только верхний, нижний остаётся живым; уровень корневой — onBack payload'а, а его
   *  нет — закрытие. Этим же ходом идут «←» шапки, Escape и свайп-назад.
   *  Необязательный: тем же типом ходят drawer-совместимые обёртки приложения
   *  (useSlidePanel), у которых стека уровней нет — там уровни ведёт натив. */
  back?: () => void;
  /** Обновить открытый drawer на месте (без body — тело сохраняется той же ссылкой,
   *  состояние не теряется): шапка мастера шаблона ведёт так счётчик шагов. */
  update: (patch: Partial<DrawerPayload>) => void;
}

/** СТРАХОВКА конца хода слоя (ТЗ ANIM-FIX О1.3/О1.4, 04.10.2026). Слой и экран под ним
 *  снимаются ПО КОНЦУ СВОЕГО ПЕРЕХОДА (`afterSlide`: transitionend/transitioncancel по
 *  transform), а не таймером под длительность: таймер 560 под прежние 0.52 с резал ход,
 *  начавшийся позже (длинный кадр), — экран под листом довозился рывком на 1–3 px
 *  (ночь 03→04.10, ~/ops/app-screens/anim-1003/REPORT.md «ДЁРГАНИЕ»). Это число — НЕ ход:
 *  ход у всех слоёв один, --m-dur 0.35 по кривой Telegram (tokens.css). Оно срабатывает, только
 *  если перехода не было вовсе (слой уже стоял в конечной точке после жеста, нет элемента). */
const SLIDE_FALLBACK_MS = 1000;

/** Фокус «от человека»: касание/клавиша в листе не раньше стольких мс до фокуса (О4 п.1). */
const USER_FOCUS_MS = 1000;

/** Страховка по заявленному ходу элемента (ТЗ WEB-CAUSES М32): не короче SLIDE_FALLBACK_MS и не
 *  короче самого длинного transition элемента + 200 мс. Иначе замедленный ход (замер кадра на 50 %
 *  хода — водитель обхода растягивает --m-dur ×10) обрывался страховкой посреди пути: .side-closing
 *  снимался через 1 с из 3,5, и плёнка закрытия возвращалась под полоску X раньше, чем гасла. */
function fallbackMs(el: Element | null | undefined): number {
  if (!el) return SLIDE_FALLBACK_MS;
  const cs = getComputedStyle(el);
  const sec = (v: string): number[] => v.split(',').map((x) => (x.trim().endsWith('ms') ? parseFloat(x) / 1000 : parseFloat(x)) || 0);
  const d = sec(cs.transitionDuration);
  const dl = sec(cs.transitionDelay);
  const longest = Math.max(0, ...d.map((v, i) => v + (dl[i % dl.length] ?? 0)));
  return Math.max(SLIDE_FALLBACK_MS, Math.round(longest * 1000) + 200);
}

/** Дождаться конца хода элемента: своё transitionend/transitioncancel по transform (события
 *  потомков не считаются) или страховка SLIDE_FALLBACK_MS. Возвращает отмену. */
function afterSlide(el: Element | null | undefined, done: () => void): () => void {
  let over = false;
  const onEnd = (e: Event): void => {
    if (e.target !== el || (e as TransitionEvent).propertyName !== 'transform') return;
    finish();
  };
  const timer = window.setTimeout(() => finish(), fallbackMs(el));
  function stop(): void {
    over = true;
    window.clearTimeout(timer);
    el?.removeEventListener('transitionend', onEnd);
    el?.removeEventListener('transitioncancel', onEnd);
  }
  function finish(): void {
    if (over) return;
    stop();
    done();
  }
  el?.addEventListener('transitionend', onEnd);
  el?.addEventListener('transitioncancel', onEnd);
  return stop;
}

/** УХОД СЛОЯ — СО СЛЕДУЮЩЕГО КАДРА (ТЗ WEB-CAUSES О1/М32, ОП-14 06.10.2026). Закрытие обычно идёт тем же
 *  касанием, что и работа хозяина слоя («Готово» сохраняет кнопку, «Удалить» перерисовывает композер):
 *  класс ухода, снятый в том же коммите, запускал часы перехода кадром, который несёт эту работу, —
 *  след WM20 «Готово»/«Удалить кнопку»: первый показанный кадр ухода сразу на 31–44 % пути, кривая
 *  закрытия не зеркало открытия («НЕСИММЕТРИЧНО»). Теперь работа касания рисуется своим кадром (под ещё
 *  стоящим слоем), а класс ухода снимается в начале следующего кадра (rAF + flushSync) — кадр старта
 *  несёт один transform. В приложении (html.app) слои рисует натив — сразу. Жест (свайп) ведёт слой
 *  своим inline-трансформом, на него кадр задержки не влияет. Открытие этим не тронуто: пробы «уровень
 *  ждёт своё тело» и «тяжёлое тело — после хода» — в UI-FIXES-JOURNAL (ОП-14), обе отвергнуты. */
function leaveAtNextFrame(go: () => void): void {
  if (typeof window === 'undefined' || document.documentElement.classList.contains('app')) {
    go();
    return;
  }
  requestAnimationFrame(() => flushSync(go));
}

const DrawerCtx = createContext<DrawerApi | null>(null);

export function useDrawer(): DrawerApi {
  const ctx = useContext(DrawerCtx);
  if (!ctx) throw new Error('useDrawer must be used within <DrawerProvider>');
  return ctx;
}

/** То же, но БЕЗ провайдера возвращает null, а не падает. Нужен компонентам, которые живут
 *  и в кабинете, и вне его (лента сценария рисуется ещё на публичном снимке /s/<токен> и в
 *  админской видео-студии): шторку такой компонент открывает, только если ей есть где
 *  открыться. */
export function useDrawerOptional(): DrawerApi | null {
  return useContext(DrawerCtx);
}

/** Уровень окна: своя запись стека (ключ живёт, пока уровень на экране). */
interface DrawerLvl {
  key: number;
  p: DrawerPayload;
  /** Уровень положил свою запись истории (`href`, pushState): снимается через popstate. */
  pushed?: boolean;
  /** ОДИНОЧНЫЙ ЛИСТ поверх бокового окна на телефоне (бывший «слой-2»): `close()` снимает только
   *  его, нижнее окно остаётся (так жила «Информация» над формой модели). 01.10.2026. */
  solo?: boolean;
}

/** Класс экрана под окном (`on`, вид — cabinet.css) и его возврат: снят — экран едет назад под
 *  классом `closing` до конца СВОЕГО хода (transform .main). Возвращает уборку для эффекта. */
function underScreen(cab: HTMLElement, on: string, closing: string, active: boolean): (() => void) | undefined {
  const was = cab.classList.contains(on);
  cab.classList.toggle(on, active);
  if (active) {
    cab.classList.remove(closing);
    return undefined;
  }
  if (!was) return undefined;
  cab.classList.add(closing);
  const stop = afterSlide(cab.querySelector('.main'), () => cab.classList.remove(closing));
  return () => {
    stop();
    cab.classList.remove(closing);
  };
}

/** Слой ведёт себя «по-телефонному» (ЗАКОН 3, ТЗ WEB-CAUSES М48): узкое окно ИЛИ вложенный уровень
 *  внутри панели 460 на широком — панель = экран телефона, её уровни живут теми же жестами и FLIP,
 *  что на телефоне. Корень-панель на широком — нет (первый уровень на широком не трогаем). */
function phoneLike(el: Element | null | undefined): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= 820 || !!el?.classList.contains('dr-lvl');
}

/** Элемент уровня по ключу (атрибут data-lvl у aside, DrawerLevel). */
function levelEl(ov: HTMLElement | null, key: number): Element | null {
  return ov?.querySelector(`:scope > [data-lvl="${key}"]`) ?? null;
}

/** Одна ли у двух окон закрытая точка (вид слоя: лист / боковое / ЧП / во весь экран).
 *  Высота листа (детент) сюда не входит: её смену на месте ведёт FLIP в DrawerLevel. */
function sameClosedPoint(a: DrawerPayload, b: DrawerPayload): boolean {
  return !!a.sheet === !!b.sheet && !!a.side === !!b.side && !!a.cp === !!b.cp && !!a.full === !!b.full;
}

/** Текущий адрес строки (путь + параметры) — сравнивать с `href` уровня. */
// ИСТОРИЯ — ПОСЛЕ ПЕРВОГО КАДРА СЛОЯ (ТЗ WEB-CAUSES О1, профиль 06.10: от касания до первого кадра
// хода 90–120 мс, длинный кадр 77 мс в обработчике ДО .show). Причина — запись истории в самом
// обработчике касания: Next перехватывает pushState/replaceState и применяет адрес своим
// действием в микрозадаче, пока window.event ещё «клик», — React получает срочное обновление и
// перерисовывает ВСЁ дерево роутера (оболочку, стек разделов) до первого кадра слоя. Теперь запись,
// сделанная из обработчика ввода, ложится в очередь и выполняется кадром позже, по порядку; пока
// она ждёт, адрес и состояние истории для самого провайдера берутся из очереди (hereHref,
// histState), а шаг «назад» сначала дописывает очередь (histFlush).
let histQ: (() => void)[] | null = null;
let histPend: { state: unknown; href: string } | null = null;

function inInputEvent(): boolean {
  const ev = typeof window === 'undefined' ? undefined : window.event;
  return !!ev && /^(click|pointer|touch|mouse|key)/.test(ev.type);
}

function histFlush(): void {
  const q = histQ;
  histQ = null;
  histPend = null;
  if (q) for (const run of q) run();
}

function histWrite(kind: 'push' | 'replace', state: unknown, href: string): void {
  const run = (): void =>
    kind === 'push' ? window.history.pushState(state, '', href) : window.history.replaceState(state, '', href);
  if (!histQ && !inInputEvent()) {
    run();
    return;
  }
  if (!histQ) {
    histQ = [];
    requestAnimationFrame(() => window.setTimeout(histFlush, 0));
  }
  histQ.push(run);
  histPend = { state, href };
}

/** Состояние истории с учётом записи, ждущей своего кадра. */
function histState(): { scrDrawer?: number } | null {
  return (histPend ? histPend.state : window.history.state) as { scrDrawer?: number } | null;
}

function hereHref(): string {
  if (typeof window === 'undefined') return '';
  return histPend ? histPend.href : `${window.location.pathname}${window.location.search}`;
}

/** Положить запись истории для уровня с адресом; true — запись положена (адрес отличался). */
function pushLevelHref(key: number, p: DrawerPayload): boolean {
  if (!p.href || typeof window === 'undefined' || hereHref() === p.href) return false;
  histWrite('push', { scrDrawer: key }, p.href);
  return true;
}

/** Верхняя кромка листа с детентом — CSS-переменной (--sheet-top читает мобильный
 *  .sheet.short в cabinet.css). Считает чистая функция lib/sheet-detent. */
function sheetVar(p: DrawerPayload | null): React.CSSProperties | undefined {
  const top = sheetTopFromDetent(p?.sheetDetent);
  return top ? ({ '--sheet-top': top } as React.CSSProperties) : undefined;
}

export function DrawerProvider({ children }: { children: React.ReactNode }) {
  const t = useTranslations('cabinet');
  // СТЕК УРОВНЕЙ (владелец 13.08.2026): [0] — корень окна, дальше уровни вглубь. У обычных
  // шторок кабинета уровень всегда один — их поведение не меняется ни на байт.
  const [levels, setLevels] = useState<DrawerLvl[]>([]);
  // Ключи уровней-НАДСТРОЕК, доехавших на место (корнем правит класс .show, как раньше).
  const [stacked, setStacked] = useState<number[]>([]);
  const [show, setShow] = useState(false);
  const lvlSeq = useRef(0);
  // Стек живёт в ref (open/back зовут друг за другом в одном тике — state в замыкании
  // протух бы) и ставится ТОЛЬКО через setLvls: ref и state меняются одной строкой.
  const levelsRef = useRef<DrawerLvl[]>([]);
  const setLvls = useCallback((next: DrawerLvl[]) => {
    levelsRef.current = next;
    setLevels(next);
  }, []);
  const payload = levels.length ? levels[levels.length - 1]!.p : null;

  // Тип модалки (sheet ↔ полная) меняет ЗАКРЫТУЮ точку старта, а на transform drawer'а
  // висит transition: без глушения класс-свап не ТЕЛЕПОРТИРУЕТ drawer в новую закрытую
  // точку, а ЗАПУСКАЕТ плавный переход из чужой (справа) — и .show ретаргетит анимацию
  // с летящего значения: шторка «выезжает справа/по диагонали». Вскрыто живой пробой
  // WebKit (replay: scratchpad/probe-sheet.mjs) 2026-07-18 после трёх слепых фиксов.
  // Порядок ЖЁСТКИЙ: transition off → класс (flushSync) → reflow → transition on → .show.
  const openSeq = useRef(0);
  // Актуальные payload/show для open() (он в useCallback без deps — state в замыкании протух бы).
  const payloadRef = useRef<DrawerPayload | null>(null);
  const showRef = useRef(false);
  // ── Слой 2: шторка ПОВЕРХ открытой полной модалки (мобила). «Информация» модели
  // открывается над формой, «назад»/крестик/свайп-вниз возвращают к нижней модалке
  // нетронутой (владелец 2026-07-18: подмена содержимого — неправильно).
  const [payload2, setPayload2] = useState<DrawerPayload | null>(null);
  const [show2, setShow2] = useState(false);
  const show2Ref = useRef(false);
  const aside2Ref = useRef<HTMLElement>(null);
  // Прокрутка страницы ПОД шторкой — снимается В МОМЕНТ ОТКРЫТИЯ, а не в эффекте замка
  // (О12, видео владельца «при закрытии дёрганье»). Эффект замка — passive: он бежит
  // после отрисовки тела шторки, а iOS к этому времени успевает утащить вьюпорт вверх на
  // высоту клавиатуры (канон IOS-KEYBOARD-CANON §1.1: vv.resize → через ~100мс scrollY
  // прыгает на 337) — замок запоминал уже СДВИНУТУЮ точку. На закрытии её возвращали, и,
  // когда клавиатура схлопывалась, окно вырастало обратно, максимум прокрутки падал на ту
  // же высоту — WebKit подрезал точку, и лента прыгала. Снимок в open() берётся ДО того,
  // как в дереве появится хоть одно поле, поэтому он всегда «докладавиатурный».
  const lockAt = useRef<{ x: number; y: number } | null>(null);
  const kickT = useRef(0);
  // Ключи уровней, которые уже уезжают (сняты, но тело ещё на экране) — см. leaveLevel.
  const leaving = useRef<Set<number>>(new Set());
  // Подложка уровней (.drawer-ov): по ней ищутся элементы уровней и экран под окном (.cab).
  const ovRef = useRef<HTMLDivElement>(null);
  const open = useCallback((p: DrawerPayload) => {
    // ОТКРЫВШАЯ СТРОКА ОТДАЁТ ФОКУС (TARIFF-PAGE-TZ §11 П10, владелец 11.10.2026: «два разных ховера» в каталоге
    // шаблонов). Строка-кнопка (div[role=button]) держала фокус всё время, пока окно открыто, а после закрытия
    // браузер рисовал на ней кольцо :focus-visible — чёрную рамку, которая читалась как второй, тёмный ховер рядом
    // с серой строкой под курсором. Открывшая строка может лежать и в нижнем уровне стопки (каталог шаблонов —
    // сам окно), поэтому фокус снимается с любой кнопки/строки; поле ввода (набор текста при `replace` того же
    // окна) не трогаем.
    if (typeof document !== 'undefined') {
      const a = document.activeElement as HTMLElement | null;
      if (a && a !== document.body && !a.matches('input, textarea, select, [contenteditable]')) a.blur?.();
    }
    // Снимок только на ПЕРВОМ открытии: пока шторка живёт (уровни стека, переоткрытие),
    // body уже зафиксирован и window.scrollY = 0 — перезапись затёрла бы правду.
    if (typeof window !== 'undefined' && !showRef.current && !show2Ref.current) {
      lockAt.current = { x: window.scrollX, y: window.scrollY };
    }
    // ── СТЕК УРОВНЕЙ — ПРАВИЛО ПО УМОЛЧАНИЮ (ТЗ «Причины» П1, 05.10.2026) ────────────────
    // При ЖИВОМ окне ЛЮБОЕ открытие кладёт НОВЫЙ уровень стопки поверх живого нижнего — с
    // именем (`winKey`) или без: лист приезжает снизу, боковое окно справа, нижний уровень
    // остаётся смонтированным, «‹» приводит в него, а не в пересобранную копию. Раньше
    // уровень заводился только у окон с именем, а прочие ПОДМЕНЯЛИ корень на месте — без
    // хода, и «‹»/«×» снимали всё окно (М21 «Настройки → Подключения», М23 «Прошёл бота»,
    // Д2.2 п.13 «страница папки»; след 05.10 P2 шаг 13). Направление и вид — прежние правила
    // HIG (владелец 25.08.2026): лист на листе и окно на окне — стопкой, окно поверх листа —
    // в раме листа, лист поверх окна — своим листом; разъезжается только картинка (cabinet.css).
    // Подмена на месте осталась ровно двум случаям:
    //  • `replace: true` или ТО ЖЕ тело (одна ссылка) — тот же экран открыт заново (смена
    //    режима, шапка по навигации внутри тела): верхний уровень меняет содержимое на месте;
    //  • имя уровня уже в стеке — возврат к нему (настройки после пикера/подключения): всё,
    //    что выше, снимается, сам уровень обновляется на месте — дублей не заводим.
    // Окно закрыто или уезжает (close() в этом же ходе) — открытие идёт новым корнем ниже.
    // ВЫЕЗД — В ПЕРВОМ ЖЕ КАДРЕ (ТЗ ANIM-FIX О1.1/О1.2, 04.10.2026): уровень монтируется в
    // закрытой точке и сам переводит себя «на место» в своём layout-эффекте после снимка стиля
    // (DrawerLevel, entered) — независимо от того, кто и откуда открыл.
    const cur = levelsRef.current;
    // Уезжающий уровень (его только что сняли, тело ещё на экране) для стопки уже не
    // существует: ни «верхним» он не считается, ни своим именем уровень не занимает.
    const liveCur = liveLevels(cur, leaving.current);
    const topL = liveCur.length ? liveCur[liveCur.length - 1]! : null;
    const live = typeof window !== 'undefined' && showRef.current && !!topL;
    // ТОТ ЖЕ ЭКРАН: флаг `replace`, та же ссылка на тело или ТОТ ЖЕ АДРЕС (Закон 2: адрес и
    // есть экран). Адрес ловит повторное открытие подстраницы «Аккаунта» её же адресом —
    // эффект глубокой ссылки (settings.tsx) открывает её второй раз, когда адрес уже встал;
    // без этого правила под страницей лёг бы её дубль, и Escape снимал бы только его.
    const sameScreen =
      !!topL && (p.replace === true || p.body === topL.p.body || (!!p.href && p.href === topL.p.href));
    if (live && topL && sameScreen && liveCur.length > 1) {
      // Тот же экран на ВЛОЖЕННОМ уровне — содержимое на месте, ключ и запись истории те же.
      if (p.href && hereHref() !== p.href) histWrite('replace', { scrDrawer: topL.key }, p.href);
      setLvls(cur.map((l) => (l.key === topL.key ? { ...l, p } : l)));
      return;
    }
    if (live && topL && !sameScreen) {
      const at = p.winKey ? cur.findIndex((l) => l.p.winKey === p.winKey && !leaving.current.has(l.key)) : -1;
      if (at >= 0) {
        const drop = new Set(cur.slice(at + 1).map((l) => l.key));
        setStacked((s) => s.filter((k) => !drop.has(k)));
        // Уровень обновляется на месте: адрес, если он у него есть, — тоже на месте (replace).
        if (p.href && hereHref() !== p.href) histWrite('replace', { scrDrawer: cur[at]!.key }, p.href);
        setLvls([...cur.slice(0, at), { key: cur[at]!.key, p, pushed: cur[at]!.pushed }]);
        return;
      }
      const key = ++lvlSeq.current;
      const pushed = pushLevelHref(key, p);
      // ОДИНОЧНЫЙ ЛИСТ (бывший «слой-2», 01.10.2026): лист без имени поверх БОКОВОГО окна на
      // телефоне — `close()` снимает только его, нижнее окно живёт (так жила «Информация» над
      // формой модели, кроппер над профилем). Прочие уровни `close()` снимает вместе с окном.
      const solo = window.innerWidth <= 820 && p.sheet === true && !p.winKey && topL.p.sheet !== true;
      setLvls([...cur, { key, p, pushed, ...(solo ? { solo: true } : {}) }]);
      setStacked((s) => [...s, key]);
      return;
    }
    openSeq.current += 1;
    const aside = asideRef.current;
    // ПЕРЕОТКРЫТИЕ ЖИВОГО КОРНЯ ТОГО ЖЕ ВИДА — БЕЗ ТЕЛЕПОРТА (ТЗ ANIM-FIX О1б Р1, 04.10.2026).
    // Телепорт (transition off → reflow → on) нужен, когда меняется ЗАКРЫТАЯ ТОЧКА (окно
    // закрыто/уезжает или сменился вид листа↔окна). Корень, который уже открыт или ещё едет
    // тем же видом, подменяет только содержимое — а глушение transition обрывало его ход:
    // тело «Создать бота» (CreateChooser) при монтировании зовёт onNav → drawer.open, и с
    // тех пор, как тело монтируется после первого кадра хода (О1.2), этот вызов приходил
    // ПОСРЕДИ выезда — лист вставал на место за кадр (пересъёмка web-fix1, WB01:28 и 15
    // маршрутов мастера: +97 мс transform 854 → 0 без хода).
    const was0 = cur.length ? cur[0]!.p : null;
    const sameRoot = showRef.current && !!was0 && sameClosedPoint(was0, p);
    const teleport = !!aside && !sameRoot;
    if (teleport) aside.style.transition = 'none';
    // Корневой уровень переиспользует свой ключ (тот же DOM-элемент): на нём и держится
    // приём «телепорт в закрытую точку» выше. Уровни-надстройки при этом снимаются.
    const rootKey = cur.length ? cur[0]!.key : ++lvlSeq.current;
    // Корень с адресом: запись истории кладётся один раз на открытие окна; переоткрытие
    // живого корня (подмена содержимого) её не дублирует — адрес меняется на месте.
    let pushed = cur.length ? !!cur[0]!.pushed : false;
    if (cur.length && p.href && hereHref() !== p.href && showRef.current) {
      histWrite('replace', { scrDrawer: rootKey }, p.href);
    } else if (!cur.length || !showRef.current) {
      pushed = pushLevelHref(rootKey, p);
    }
    flushSync(() => {
      setLvls([{ key: rootKey, p, pushed }]);
      setStacked([]);
    });
    if (teleport) {
      void aside.offsetWidth;
      aside.style.transition = '';
    }
    // Класс .show подложке ставит НЕ этот вызов, а layout-эффект провайдера (ovOn ниже) — после
    // снимка закрытой точки: корень, смонтированный тем же коммитом (первое открытие, открытие
    // из эффекта, где flushSync не сливает), иначе вставал на место за кадр без хода.
    setShow(true);
  }, [setLvls]);

  /** Снять окно целиком в DOM (без истории): общий хвост close() и popstate. */
  const dismissAll = useCallback(() => {
    openSeq.current += 1;
    // Окно уже не живое для open() этого же хода: «закрыть и сразу открыть другое» (форма
    // подключена → вопрос про нишу) открывает новый корень, а не уровень поверх уходящего.
    showRef.current = false;
    const seq = openSeq.current;
    leaveAtNextFrame(() => {
      // Пока ждали кадр, открыли новое окно (open() сдвинул openSeq) — уход отменён, оно уже своё.
      if (openSeq.current !== seq) return;
      setStacked([]); // уровни-надстройки уезжают вправо вместе с корнем — окно закрылось целиком
      setShow(false);
    });
  }, []);

  /** Снять вложенный уровень по ключу: выезд, а тело — ПОСЛЕ выезда (таймер размонтирования).
   *  Пока уровень уезжает, он помечен в `leaving` — для счёта уровней его уже нет
   *  (lib/drawer-levels.ts): второй вызов подряд снимает СЛЕДУЮЩИЙ уровень, а не этот же.
   *  СНЯТЫЙ УРОВЕНЬ МОГ ПЕРЕСТАТЬ БЫТЬ ВЕРХНИМ (ТЗ WEB-LOOK Э9 ход 2): пока он уезжал,
   *  поверх уже открыли новый — «назад» и сразу «открыть» в одном ходе («⋯ →
   *  Редактировать» у строки листа «Вложение», выбор файла сразу после листа). Поэтому из
   *  стопки он убирается по ключу, а не «верхним». */
  const leaveLevel = useCallback((key: number) => {
    leaving.current.add(key);
    leaveAtNextFrame(() => setStacked((s) => s.filter((k) => k !== key)));
    afterSlide(levelEl(ovRef.current, key), () => {
      leaving.current.delete(key);
      const ls = levelsRef.current;
      if (ls.length > 1 && ls.some((l) => l.key === key)) setLvls(ls.filter((l) => l.key !== key));
    });
  }, [setLvls]);

  // INVARIANT(scriptera): ШАГ ИСТОРИИ НАЗАД ДЛЯ СНЯТИЯ УРОВНЯ ДЕЛАЕТСЯ ОДИН РАЗ. `history.go`
  // асинхронен: до popstate `history.state` ещё прежний, и второй close()/back() подряд (два
  // вызова из эффекта, двойной клик, Escape следом за «×») делал второй шаг — человека
  // выбрасывало с раздела на предыдущую страницу. Причина: найдено пробой 01.10.2026 (бот без
  // мастера закрывал панель дважды). Канон: ~/projects/WEB-LOOK-TZ.md, Э0.4.
  const histBusy = useRef(false);
  const histStep = useCallback((n: number): void => {
    if (histBusy.current) return;
    histBusy.current = true;
    window.setTimeout(() => {
      histBusy.current = false;
    }, 1000);
    histFlush(); // запись слоя, ждущая кадра, — сперва в историю, потом шаг назад
    window.history.go(-n);
  }, []);

  const close = useCallback(() => {
    // Закрывается ВЕРХНИЙ слой: шторка-поверх уходит, нижняя модалка остаётся.
    if (show2Ref.current) {
      setShow2(false);
      return;
    }
    // Одиночный лист (бывший слой-2) поверх бокового окна: close() снимает только его.
    {
      const cur0 = liveLevels(levelsRef.current, leaving.current);
      const top0 = cur0[cur0.length - 1];
      if (cur0.length > 1 && top0?.solo) {
        if (top0.pushed && typeof window !== 'undefined') {
          const st = histState();
          if (st?.scrDrawer === top0.key) {
            histStep(1);
            return;
          }
        }
        leaveLevel(top0.key);
        return;
      }
    }
    // АДРЕС (ТЗ WEB-LOOK Э0.4): уровни с записями истории снимаются ШАГОМ ИСТОРИИ назад —
    // DOM уберёт обработчик popstate, тем же путём, что кнопка «назад» браузера. Записей нет,
    // но корень открыт по прямому заходу с `closeHref` — адрес возвращается заменой.
    // Счёт — по ЖИВЫМ уровням: уезжающий (снятый «назад» уровень с адресом) свою запись истории
    // уже отдал, второй шаг назад за него выбросил бы человека с раздела.
    const cur = liveLevels(levelsRef.current, leaving.current);
    const n = cur.filter((l) => l.pushed).length;
    if (n > 0 && typeof window !== 'undefined') {
      const st = histState();
      const top = cur[cur.length - 1];
      if (top && st?.scrDrawer === top.key) {
        // Окно снимет popstate, но для open() этого же хода оно уже закрыто (см. dismissAll):
        // иначе новый экран лёг бы уровнем поверх окна, которое сейчас уйдёт.
        showRef.current = false;
        histStep(n);
        return;
      }
      // История ушла дальше (человек навигировал мимо окна) — снимаем только DOM.
    } else if (cur[0]?.p.closeHref && typeof window !== 'undefined') {
      histWrite('replace', null, cur[0].p.closeHref);
    }
    dismissAll();
  }, [dismissAll, leaveLevel, histStep]);

  // INVARIANT(scriptera): «ЗАКРЫТЬ» ПОВЕРХНОСТИ СНИМАЕТ ОДИН ВЕРХНИЙ УРОВЕНЬ, А НЕ ОКНО. Окно
  // закрывается, только когда уровень в нём один. Причина: вложенные страницы (триггер и
  // сообщение мастера, окно «Сообщение» рассылки) зовут `panel.close()`, и пока оно снимало
  // окно целиком, выбор триггера закрывал весь мастер, а «Готово» сообщения — всю форму
  // рассылки (дефект К3.1, был на проде). В iOS-приложении то же «закрыть» — pop одного слоя.
  // Канон: ~/projects/WEB-PARITY-3-TZ.md, раздел 3, К3.1.
  const closeLevel = useCallback(() => {
    if (show2Ref.current) {
      setShow2(false);
      return;
    }
    const step = closeStep(levelsRef.current, leaving.current);
    if (step.kind === 'window') {
      close();
      return;
    }
    // Уровень с записью истории — шаг истории назад, DOM снимет popstate (Э0.4).
    if (step.level.pushed && typeof window !== 'undefined') {
      const st = histState();
      if (st?.scrDrawer === step.level.key) {
        histStep(1);
        return;
      }
    }
    leaveLevel(step.level.key);
  }, [close, leaveLevel, histStep]);

  /** Снять поток уровней: нижний живой уровень с именем из `names` и всё над ним (DrawerApi). */
  const closeFrom = useCallback(
    (names: readonly string[], opts?: { lowerKeepDeck?: boolean }) => {
      if (show2Ref.current) {
        setShow2(false);
        return;
      }
      const cur = liveLevels(levelsRef.current, leaving.current);
      const at = cur.findIndex((l) => !!l.p.winKey && names.includes(l.p.winKey));
      if (at < 0) {
        closeLevel();
        return;
      }
      if (at === 0) {
        close();
        return;
      }
      const drop = cur.slice(at);
      const top = drop[drop.length - 1]!;
      const st = typeof window !== 'undefined' ? histState() : null;
      const pushedN = drop.filter((l) => l.pushed).length;
      // Уровни без адреса уезжают сами; уровни с адресом отдают свои записи истории одним
      // шагом назад — их снимет popstate тем же выездом (Э0.4).
      const viaHistory = pushedN > 0 && st?.scrDrawer === top.key;
      // НИЖНИЕ УРОВНИ ПОТОКА УХОДЯТ, НЕ РАЗЖИМАЯСЬ (`lowerKeepDeck`, ТЗ WEB-CAUSES О4, 06.10.2026): уровень
      // из колоды (масштаб 0,92) прежде на уходе разжимался в 1 и ехал вбок на 16 px, пока уезжал вниз
      // (обход: «НЕ С ТОЙ СТОРОНЫ» WM01/WM30 на «Добавить» редактора). Теперь он уезжает вниз тем же
      // ходом в своём масштабе колоды — по горизонтали стоит. Стилем, а не классом: класс уровня React
      // перепишет тем же кадром (снятие меняет стопку).
      if (opts?.lowerKeepDeck) {
        for (const l of drop) {
          const el = l.key !== top.key ? (levelEl(ovRef.current, l.key) as HTMLElement | null) : null;
          if (!el) continue;
          // Масштаб — тот, в котором уровень стоит сейчас (0,92 в колоде, 1 у полулиста над полулистом).
          const m = /^matrix\(([-\d.e]+)/.exec(getComputedStyle(el).transform);
          const k = m ? Number(m[1]) : 1;
          el.style.transform = `translateY(calc(100% + 40px)) scale(${Number.isFinite(k) && k > 0 ? k : 1})`;
        }
      }
      for (const l of drop) if (!viaHistory || !l.pushed) leaveLevel(l.key);
      if (viaHistory) histStep(pushedN);
    },
    [close, closeLevel, leaveLevel, histStep],
  );

  /** Назад НА УРОВЕНЬ ВЫШЕ: снимаем только верхний уровень, нижний остаётся живым.
   *  Корневой уровень — прежнее поведение: onBack payload'а, а его нет — закрыть окно. */
  const back = useCallback(() => {
    if (show2Ref.current) {
      setShow2(false);
      return;
    }
    // Счёт — по ЖИВЫМ уровням: уезжающий (только что снятый) второй раз не снимается.
    const cur = liveLevels(levelsRef.current, leaving.current);
    // Верхний уровень с записью истории — шаг истории назад, DOM снимет popstate (Э0.4).
    const topP = cur[cur.length - 1];
    if (topP?.pushed && typeof window !== 'undefined') {
      const st = histState();
      if (st?.scrDrawer === topP.key) {
        histStep(1);
        return;
      }
    }
    if (cur.length > 1) {
      leaveLevel(cur[cur.length - 1]!.key);
      return;
    }
    // Корневой уровень ОКНА КОНСТРУКТОРА со своей «назад» (каталог действий из пикера
    // блоков, подмена содержимого на десктопе) — идём его ходом. Признак окна — winKey
    // (раньше проверялся `side`: до 25.08.2026 окна конструктора были горизонтальными, и
    // после перевода их на листы проверка перестала бы срабатывать — «назад» с корня
    // закрывало бы окно вместо возврата). У прочих шторок кабинета winKey нет, и
    // Escape/жест закрывают окно, как и раньше.
    const root = cur[0]?.p;
    if (root && (root.side || root.winKey) && root.onBack) {
      root.onBack();
      return;
    }
    close();
  }, [close, leaveLevel, histStep]);

  // Правка открытого drawer на месте (шапка мастера: счётчик шагов). Без body тело
  // остаётся той же ссылкой — React поддерево не перемонтирует; попутный скролл-ап
  // эффекта уровня на смене шага и нужен (новый экран — с верха).
  const update = useCallback(
    (patch: Partial<DrawerPayload>) => {
      const ls = levelsRef.current;
      // Правится верхний ЖИВОЙ уровень: уезжающий (страница мастера после «Готово») ещё в
      // стопке, но шапка, которую ведут снаружи (счётчик шагов), принадлежит уровню под ним.
      const live = liveLevels(ls, leaving.current);
      const last = live[live.length - 1];
      if (!last) return;
      // Запись истории уровня (`pushed`, Э0.4) правкой на месте не теряется: без неё
      // закрытие после update не делало бы шаг истории назад и адрес оставался бы висеть.
      setLvls(ls.map((l) => (l.key === last.key ? { ...l, p: { ...l.p, ...patch } } : l)));
    },
    [setLvls],
  );
  payloadRef.current = payload;
  showRef.current = show;
  show2Ref.current = show2;

  // Размонтирование тела слоя-2 после анимации закрытия (как у слоя-1).
  useEffect(() => {
    if (show2 || payload2 === null) return;
    return afterSlide(aside2Ref.current, () => setPayload2(null));
  }, [show2, payload2]);

  // Размонтировать тела всех уровней ПО КОНЦУ ХОДА КОРНЯ (afterSlide; ход — --m-dur, cabinet.css):
  // иначе PayFrame / админ-поллинг продолжают жить в скрытом drawer бессрочно.
  useEffect(() => {
    if (show || levels.length === 0) return;
    return afterSlide(asideRef.current, () => {
      leaving.current.clear();
      setLvls([]);
      setStacked([]);
    });
  }, [show, levels.length, setLvls]);

  // POPSTATE (ТЗ WEB-LOOK Э0.4): запись истории верхнего уровня ушла (кнопка «назад»
  // браузера, наш back()/close() через history) — снимаем уровни сверху, пока верхний
  // «положен» и его ключ не равен ключу в history.state. Корень без записи не трогаем.
  useEffect(() => {
    function onPop() {
      histBusy.current = false;
      const st = histState();
      // Счёт — по ЖИВЫМ уровням: уезжающие своё уже отдали истории.
      let cur = liveLevels(levelsRef.current, leaving.current);
      while (cur.length > 0) {
        const top = cur[cur.length - 1]!;
        if (!top.pushed || st?.scrDrawer === top.key) break;
        if (cur.length === 1) {
          dismissAll();
          cur = [];
          break;
        }
        // СНЯТИЕ ВЫЕЗДОМ, а не вырезкой из стопки (ТЗ ANIM-FIX О1.1, 04.10.2026): прежде уровень
        // с адресом («Новая рассылка», подстраницы окна бота) по «назад»/Escape убирался из
        // стопки сразу — тело исчезало за кадр без хода (ночь 03→04.10: «sheet close — путь 0 px
        // за 0 кадров»). Теперь он уезжает тем же ходом, что уровень без адреса (leaveLevel).
        leaveLevel(top.key);
        cur = cur.slice(0, -1);
      }
      // Оба правила ниже — только браузер: в приложении слои ведёт натив, его не трогаем.
      if (document.documentElement.classList.contains('app')) return;
      const key = st?.scrDrawer;
      const live = typeof key === 'number' && cur.some((l) => l.key === key);
      // «НАЗАД» ОТ ПАНЕЛИ, ОТКРЫТОЙ ПРЯМЫМ ЗАХОДОМ ПО АДРЕСУ (своей записи истории у корня нет):
      // адрес ушёл с адреса панели — панель снимается, иначе она висела бы над чужим адресом.
      const root = cur[0];
      if (root && !root.pushed && root.p.href && hereHref() !== root.p.href && !live) {
        dismissAll();
        return;
      }
      // «ВПЕРЁД» НА ЗАПИСЬ ПАНЕЛИ, КОТОРОЙ УЖЕ НЕТ (закрыли панель и нажали «вперёд» в
      // браузере): адрес панели встал, а уровня в стопке нет — содержимое по ключу не
      // восстановить. Закон 2 (адрес открывает то же окно) исполняет загрузка страницы по
      // этому адресу — та же дорога, что Cmd+R.
      if (typeof key === 'number' && !live) window.location.reload();
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [dismissAll, leaveLevel]);

  // «НАЗАД» РУКОЙ МИМО ШАПКИ (Escape, клик мимо окна) — ЧЕРЕЗ ВОПРОС ВЕРХНЕГО УРОВНЯ (ТЗ
  // «Причины» П1 п.2, 05.10.2026): у уровня с `closeAsk` («Отменить создание?») Escape и клик
  // мимо спрашивают так же, как «‹»/«×»/смахивание (`guarded` в DrawerLevel). Раньше они
  // звали back() напрямую и молча снимали мастер с введённым.
  const backAsked = useCallback(() => {
    if (show2Ref.current) {
      back();
      return;
    }
    const cur = liveLevels(levelsRef.current, leaving.current);
    const ask = cur[cur.length - 1]?.p.closeAsk;
    if (!ask) {
      back();
      return;
    }
    void ask().then((ok) => {
      if (ok) back();
    });
  }, [back]);

  // Escape — «назад на уровень» (владелец 13.08.2026), а не «закрыть всё»: у окна с одним
  // уровнем это ровно прежнее закрытие.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Окно поверх слоя (KDialog — календарь, барабан, вопрос) Escape уже закрыло: Radix
      // ловит его раньше и гасит preventDefault. Слой под окном не трогаем — иначе одно
      // нажатие снимало окно даты И всю форму рассылки (П13, кадр R04-broadcast-date-picker).
      if (e.key === 'Escape' && !e.defaultPrevented && (showRef.current || show2Ref.current)) backAsked();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [backAsked]);

  // Нативная капсула-меню приложения прячется на время ВЕБ-модалки (владелец 2026-07-18):
  // натив рисует меню поверх веб-контента, веб-шторка накрыть его не может. При открытых
  // НАТИВНЫХ слоях натив это желание копит и применяет после снятия стопки (pendingBarHidden).
  // Веб-панель Safari, наоборот, не прячется — шторка наезжает на неё сверху по z
  // (владелец 2026-08-02), скрытие только при клавиатуре (правило в cabinet.css).
  // При закрытии учитываем фулскрин сценария: там меню остаётся скрытым.
  useEffect(() => {
    const b = nativeBridge();
    if (!b?.setTabBarHidden) return;
    if (!document.documentElement.classList.contains('native-tabs')) return;
    const focus = !!document.querySelector('.cab.cab-focus');
    void b.setTabBarHidden({ hidden: show || focus });
  }, [show]);

  // Скролл-лок фона на время открытого листа (стандарт bottom-sheet, как в Vaul):
  // без него iOS при фокусе инпута прокручивает ВЕСЬ документ вверх — «контент уезжает».
  // Техника position:fixed на body — единственная надёжная на iOS Safari.
  useEffect(() => {
    if (!show) return;
    // ЛОЧИТЬ НЕЧЕГО, если документ не скроллится (фикс-коробка чата сценария):
    // position:fixed на body в standalone-PWA (iOS 26) РОНЯЕТ cover-вьюпорт ровно
    // на верхний env (замер самописца 13:21: ih 874→812 в момент открытия листа,
    // без фокуса) и НЕ возвращает после снятия — «всё поднялось на высоту строки
    // и не опустилось». Без лока бага нет, а фон в фикс-коробке и так неподвижен.
    if (document.documentElement.scrollHeight <= window.innerHeight + 1) return;
    const b = document.body;
    // Точка, снятая в open() ДО монтирования тела шторки (см. lockAt): к моменту этого
    // эффекта iOS уже мог утащить вьюпорт под клавиатуру. Снимка нет (шторку открыли не
    // через open — тесты) — берём текущую прокрутку, как раньше.
    const { x, y } = lockAt.current ?? { x: window.scrollX, y: window.scrollY };
    window.clearTimeout(kickT.current); // отложенный возврат прошлой шторки — не поверх новой
    b.style.position = 'fixed';
    b.style.top = `-${y}px`;
    b.style.left = '0';
    b.style.right = '0';
    b.style.width = '100%';
    return () => {
      // Сначала закрыть клавиатуру: если разлочить body при открытой клавиатуре,
      // WebKit оставляет layout-вьюпорт сжатым и fixed-слои (нижнее меню) повисают
      // поднятыми над низом экрана (баг iOS, виден на страницах без скролла).
      (document.activeElement as HTMLElement | null)?.blur?.();
      b.style.position = '';
      b.style.top = '';
      b.style.left = '';
      b.style.right = '';
      b.style.width = '';
      window.scrollTo(x, y);
      // Пинок вьюпорту ПОСЛЕ анимации закрытия клавиатуры: заставляет WebKit пересчитать
      // layout-вьюпорт и вернуть fixed-слои на место (документированный обход). Пинок
      // ВОЗВРАЩАЕТ ТУ ЖЕ ТОЧКУ (раньше повторял текущую, то есть закреплял подрезанную):
      // окно к этому моменту выросло обратно, и если WebKit успел подрезать прокрутку под
      // сжатый вьюпорт, лента встаёт на место одним движением, а не остаётся прыгнувшей.
      lockAt.current = null;
      kickT.current = window.setTimeout(() => window.scrollTo(x, y), 350);
    };
  }, [show]);


  // КОЛОДА (ТЗ WEB-LOOK Э5): открыт вертикальный лист (`sheet: true`) — экран под ним
  // уходит назад (масштаб, шаг вверх, темнеет) классом .has-sheet на своём .cab; числа —
  // роли --m-deck-* (tokens.css), вид — cabinet.css. Только браузер: в приложении колоду
  // рисует натив (SheetDeck), класс там не ставится вовсе. --deck-y — точка прокрутки
  // (документ под листом зажат position:fixed): масштаб идёт от верха ЭКРАНА, а не от
  // верха документа. При закрытии --deck-y не снимается — иначе возврат прыгнул бы.
  // ОДИН ЗАКОН НА ВСЕ ЛИСТЫ (владелец 07.10.2026, ТЗ WEB-CAUSES М87/М90/М92): открыт любой
  // вертикальный лист — колода есть всегда; флаг `deck` вызова в браузере не читается (он — тип
  // родного слоя). Прежняя ветка «лист без колоды» (`deck: false`, М82, плёнка .has-film) снята.
  const deck = show && levels.some((l) => l.p.sheet === true);
  // ЗАКОН 3 (контейнер, не окно): пока открыт хоть один слой, открыватели считают себя «на
  // телефоне» (readPhoneLook) — вложенный лист из панели на десктопе едет как на телефоне.
  useEffect(() => {
    setFramesOpen(show ? levels.length : 0);
    return () => setFramesOpen(0);
  }, [show, levels.length]);
  // ЭКРАН ПОД БОКОВЫМ СЛОЕМ (ТЗ ANIM-FIX О1.5, решение владельца 04.10.2026 «веб = iOS»):
  // корень окна — горизонтальная шторка (не лист) → экран под ней уезжает влево на
  // --m-parallax и темнеет на --m-dim, как уходящий экран iOS (LayerGeom parallax 0.3,
  // dimAlpha 0.4). Класс .has-side на своём .cab, вид — cabinet.css; только браузер.
  const sideUnder = show && levels.length > 0 && levels[0]!.p.sheet !== true;
  // ЗАКОН 3 (контейнер, не окно): пока открыт хоть один слой, открыватели считают себя «на
  // телефоне» (readPhoneLook) — вложенный лист из панели на десктопе едет как на телефоне.
  useEffect(() => {
    setFramesOpen(show ? levels.length : 0);
    return () => setFramesOpen(0);
  }, [show, levels.length]);
  // Классы экрана под окном ставятся В ТОМ ЖЕ КАДРЕ, что .show корня (layout-эффект, ovOn
  // ниже): экран и слой начинают ход одним кадром.
  // ЗАКРЫТИЕ — ОДНО ДВИЖЕНИЕ СО СЛОЕМ: на время ухода слоя экран держит вид карточки (углы,
  // фон) и возвращается тем же ходом (.deck-closing / .side-closing, cabinet.css). Класс
  // снимается ПО КОНЦУ ХОДА САМОГО ЭКРАНА (afterSlide по transform .main), а не таймером:
  // таймер 560 срезал ход, начавшийся позже, и экран довозился рывком (ТЗ ANIM-FIX О1.4).
  useLayoutEffect(() => {
    const cab = ovRef.current?.closest<HTMLElement>('.cab');
    if (!cab || document.documentElement.classList.contains('app')) return;
    if (deck) cab.style.setProperty('--deck-y', `${lockAt.current?.y ?? window.scrollY}px`);
    return underScreen(cab, 'has-sheet', 'deck-closing', deck);
  }, [deck]);
  useLayoutEffect(() => {
    const cab = ovRef.current?.closest<HTMLElement>('.cab');
    if (!cab || document.documentElement.classList.contains('app')) return;
    return underScreen(cab, 'has-side', 'side-closing', sideUnder);
  }, [sideUnder]);

  // .show ПОДЛОЖКИ — ТОЛЬКО ПОСЛЕ СНИМКА ЗАКРЫТОЙ ТОЧКИ (ТЗ ANIM-FIX О1.1, 04.10.2026). Корень,
  // смонтированный тем же коммитом, в котором подложка получила .show (первое открытие окна,
  // открытие по адресу или из эффекта — там flushSync в open() не сливает), не имел
  // «прошлого» стиля, и браузер ставил его сразу на место: лист вставал за кадр, без хода, а
  // повтор того же открытия (уровень ещё не размонтирован) ехал — «первый раз нет, второй
  // да». Теперь: коммит с корнем в закрытой точке → снимок стиля (offsetWidth) → .show тем же
  // кадром, до отрисовки.
  const [ovOn, setOvOn] = useState(false);
  useLayoutEffect(() => {
    if (show && !ovOn) {
      void ovRef.current?.offsetWidth;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- замер стиля → класс до отрисовки (канонный приём useLayoutEffect)
      setOvOn(true);
    } else if (!show && ovOn) setOvOn(false);
  }, [show, ovOn]);

  // Ссылка на КОРНЕВОЙ уровень: на нём держится приём «телепорт в закрытую точку» в open().
  // Жесты и клавиатурный прокси живут у каждого уровня свои (DrawerLevel ниже).
  const asideRef = useRef<HTMLElement | null>(null);
  useSheetKeyboardProxy(aside2Ref, show2); // поля в шторке-поверх (напр. имейл приглашения)

  useSheetDrag(aside2Ref, show2, close);

  const api = useMemo<DrawerApi>(
    () => ({ open, close, closeLevel, closeFrom, back, update }),
    [open, close, closeLevel, closeFrom, back, update],
  );

  return (
    <DrawerCtx.Provider value={api}>
      {children}
      <div
        ref={ovRef}
        /* cp-ov = БЕЗ затемнения у панелей/модалок ЧП (как в ChatPlace, на любой ширине).
           Прежний grab-ov (лист без затемнения на мобиле, К35) снят вместе с флагом `grab`
           (ТЗ WEB-CAUSES М92): ручку и плёнку под листом даёт хост, у вызовов флагов вида нет. */
        className={`drawer-ov${show && ovOn ? ' show' : ''}${payload?.cp ? ' cp-ov' : ''}`}
        /* Клик мимо окна = НАЗАД НА УРОВЕНЬ, ровно как Escape (Р13, ревью 18.08). Раньше
           он звал close() и на десктопе сносил ВЕСЬ стек окон конструктора, хотя Escape
           рядом снимал один уровень — два разных ответа на одно и то же «уйти отсюда».
           Канон docs/NATIVE-SLIDE-PLAN.md §11: вложенность стопкой, снятие по уровню;
           закрыть всё целиком — крестик. У окна с одним уровнем back() = прежнее закрытие,
           поэтому на телефоне (там уровень обычно один) поведение не меняется. */
        onClick={(e) => {
          if (e.target === e.currentTarget) backAsked();
        }}
        /* ТАП МИМО НИЗКОГО ЛИСТА — ТОЛЬКО ЗАКРЫТИЕ (владелец 09.10.2026, «Уведомления» → группы на
           телефоне в браузере): пока сверху вложенный низкий лист, тап по уровню под ним гасится и
           снимает лист — второй лист из строки за ним не открывается (решение — lib/drawer-levels). */
        onClickCapture={(e) => {
          const hit = (e.target as Element | null)?.closest?.('[data-lvl]');
          const hitKey = hit && hit.parentElement === ovRef.current ? Number(hit.getAttribute('data-lvl')) : null;
          const phone = window.innerWidth <= 820 && !document.documentElement.classList.contains('app');
          if (outsideTap(levelsRef.current, leaving.current, hitKey, phone) !== 'close') return;
          e.preventDefault();
          e.stopPropagation();
          backAsked();
        }}
      >
        {levels.map((l, i) => (
          <DrawerLevel
            key={l.key}
            p={l.p}
            /* Верхний уровень — единственный живой: его жесты, его клавиатурный прокси.
               Нижние остаются смонтированными (состояние каталога живо), но накрыты. */
            top={i === levels.length - 1}
            nested={i > 0}
            open={i === 0 ? show : stacked.includes(l.key)}
            rootOn={i === 0 && show && ovOn}
            lvlKey={l.key}
            rootRef={i === 0 ? asideRef : undefined}
            onBackLevel={back}
            onClose={close}
          />
        ))}
      </div>
      {/* Слой 2: шторка поверх открытой полной модалки (только мобила, см. open()). */}
      <div
        className={`drawer-ov ov2${show2 ? ' show' : ''}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) close();
        }}
      >
        <aside
          ref={aside2Ref}
          className={`drawer sheet${payload2?.sheetDetent ? ' short' : ''}`}
          style={sheetVar(payload2)}
          role="dialog"
          aria-modal="true"
          aria-label={payload2?.title}
        >
          <div className="dr-head">
            <KBareButton type="button" className="dr-close" onClick={close} aria-label={t('back')}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M19 12H5M11 6l-6 6 6 6" />
              </svg>
            </KBareButton>
            <h3>{payload2?.title}</h3>
            {payload2?.headerRight ? (
              payload2.headerRight
            ) : payload2?.actionLabel ? (
              <KBareButton
                type="button"
                className="dr-act"
                title={payload2.actionLabel}
                aria-label={payload2.actionLabel}
                onClick={() => payload2.onAction?.()}
              >
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.3} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M4.5 12.5l5 5 10-11" />
                </svg>
              </KBareButton>
            ) : (
              <span className="dr-act hidden" aria-hidden="true" />
            )}
          </div>
          <div className="dr-body">{payload2?.body}</div>
        </aside>
      </div>
    </DrawerCtx.Provider>
  );
}

/**
 * ОДИН УРОВЕНЬ окна: та же шторка, что была, но своя на каждый уровень стека — со своими
 * жестами и клавиатурным прокси. Корень (nested=false) едет классом .show подложки, как
 * раньше; уровень-надстройка приезжает справа своим классом (.dr-lvl/.dr-lvl-on), поэтому
 * нижний уровень остаётся ЖИВЫМ под ним — «←» приводит в него, а не в пересобранную копию.
 */
function DrawerLevel({
  p,
  top,
  nested,
  open,
  rootOn,
  lvlKey,
  rootRef,
  onBackLevel,
  onClose,
}: {
  p: DrawerPayload;
  top: boolean;
  nested: boolean;
  open: boolean;
  /** Корень: подложка уже несёт .show (снимок закрытой точки сделан, ovOn провайдера). */
  rootOn: boolean;
  lvlKey: number;
  rootRef?: React.MutableRefObject<HTMLElement | null>;
  onBackLevel: () => void;
  onClose: () => void;
}) {
  const t = useTranslations('cabinet');
  const asideRef = useRef<HTMLElement | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const setAside = (el: HTMLElement | null): void => {
    asideRef.current = el;
    if (rootRef) rootRef.current = el;
  };

  // УРОВЕНЬ-НАДСТРОЙКА ЕДЕТ ВСЕГДА (ТЗ ANIM-FIX О1.1, 04.10.2026): монтируется в закрытой
  // точке (.dr-lvl), снимок стиля (offsetWidth) — и только потом .dr-lvl-on, тем же кадром до
  // отрисовки. Без снимка уровень, пришедший в одном коммите с «на месте», вставал за кадр.
  const [entered, setEntered] = useState(false);
  useLayoutEffect(() => {
    if (!nested || !open || entered) return;
    void asideRef.current?.offsetWidth;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- замер стиля → класс до отрисовки (канонный приём useLayoutEffect)
    setEntered(true);
  }, [nested, open, entered]);
  const on = nested ? open && entered : rootOn;

  // ТЕЛО ДОРИСОВЫВАЕТСЯ ПО ХОДУ (ТЗ ANIM-FIX О1.2, 04.10.2026: от касания до старта хода
  // медиана 33 мс, максимум 654 — рендер тела шёл ДО выезда). Новый уровень выезжает пустой
  // рамой (шапка уже на месте), тело монтируется сразу после первого кадра хода: ход идёт
  // на компоновщике, тяжёлый рендер тела его не задерживает. Подмена тела на месте (тот же
  // уровень, update/переоткрытие корня) — без задержки: уровень уже смонтирован. В приложении
  // (html.app) слои рисует натив — там тело как прежде, сразу.
  const [bodyOn, setBodyOn] = useState(() => document.documentElement.classList.contains('app'));
  useEffect(() => {
    if (bodyOn) return;
    let tm = 0;
    // ТЕЛО — НЕСРОЧНЫМ РЕНДЕРОМ (ТЗ WEB-CAUSES О1, 05.10.2026): прежний flushSync монтировал тело
    // одним куском 80–115 мс (до 360) сразу после первого кадра хода — главный поток стоял, и
    // следующий снятый кадр слоя был уже на 16–39 % пути («ПЕРВЫЙ КАДР ПОЗДНО», «РЫВОК», «КРИВАЯ»
    // в обоих движках; на iPhone тот же длинный кадр — журнал М42). startTransition режет рендер
    // тела на куски по ~5 мс и отдаёт кадры ходу; шапка уровня уже на месте с первого кадра.
    const raf = requestAnimationFrame(() => {
      tm = window.setTimeout(() => startTransition(() => setBodyOn(true)), 0);
    });
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(tm);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- один раз на монтирование уровня
  }, []);

  // Каждый новый payload — новый экран (вложенные экраны пикеров навигируют через open):
  // скролл наверх, иначе «проваливаешься» в категорию на глубину прошлой прокрутки.
  // Скроллим и тело, и внутренний .pick-list (у пикеров скроллится он, тело зажато).
  // Зависимость — ТЕЛО, а не весь payload: `update({ title / headerRight })` на месте
  // (имя бота доехало, тумблер встал в шапку — Б12) экран не меняет, и сбрасывать его
  // прокрутку нельзя (пробa 03.10.2026: лист окна бота прыгал наверх при каждой правке шапки).
  const pBody = p?.body;
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    body.scrollTo(0, 0);
    body.querySelector('.pick-list')?.scrollTo(0, 0);
  }, [pBody]);
  // ПОЛУЛИСТ РАСТЁТ ДО ПОЛНОГО, КОГДА КАРЕТКА ВСТАЛА В ПОЛЕ (круг 3, П5; кадры iOS
  // W33-tg-trigger-link/-exact/-keyword): родной лист с детентами [.medium(), .large()]
  // под клавиатурой штатно разворачивается до полной высоты (UIKit; WizardScreen
  // focusSoleField), и страница триггера на кадрах стоит во весь экран. Веб делает то же:
  // фокус в текстовом поле короткого листа снимает детент до нового содержимого листа.
  const [grown, setGrown] = useState(false);
  useEffect(() => {
    setGrown(false);
  }, [pBody]);
  const detent = !!(p?.sheet && p.sheetDetent);
  // СЛУШАТЕЛЬ — ТОЛЬКО ПОСЛЕ МОНТИРОВАНИЯ ТЕЛА (ТЗ ANIM-FIX О1б Р3, 04.10.2026). Растёт лист от
  // каретки, которую ПОСТАВИЛ человек. Поле с autoFocus ставит её само в момент монтирования
  // тела — до О1 тело монтировалось вместе с уровнем, раньше этого слушателя, и полулист
  // оставался полулистом. С тех пор как тело монтируется после первого кадра хода (О1.2),
  // автофокус ловился, и вложенный полулист («Добавить метку») на ходу рос в полный лист:
  // рамка прыгала по x на поле 8 и по y на 417 (пересъёмка web-fix1, WK23:17/30). Эффект
  // зависит от bodyOn: автофокус коммита тела проходит до подписки, как прежде.
  // КАРЕТКА ПОСРЕДИ ХОДА — РОСТ ПОСЛЕ ПРИХОДА: лист сперва доезжает полулистом, потом растёт
  // (как родной лист: детент меняется у стоящего листа). Рост посреди хода прыгал: закрытая
  // точка хода задана в долях высоты листа, и с новой высотой ход пересчитывался с другого места.
  useEffect(() => {
    const aside = asideRef.current;
    if (!aside || !detent || !bodyOn) return;
    let stop: (() => void) | null = null;
    // РОСТ — ТОЛЬКО ОТ ФОКУСА, КОТОРЫЙ ПОСТАВИЛ ЧЕЛОВЕК (ТЗ WEB-CAUSES О4 п.1, 05.10.2026): листы
    // «Вложение»/«Голосовое»/«Отрезок» ставят фокус программно после прихода, и лист рос вторым
    // движением через 150–280 мс после конца хода («ДЁРГАНИЕ» ×10, WM01/WM30). Родной лист растёт
    // только с клавиатурой, которую вызвал палец. Признак человека — касание/клавиша в этом листе
    // не раньше USER_FOCUS_MS до фокуса (тап по полю, Tab; фокус-прокси клавиатуры — тоже от тапа).
    let lastUser = -Infinity;
    const onUser = (): void => {
      lastUser = performance.now();
    };
    const onFocus = (e: FocusEvent) => {
      const el = e.target as HTMLElement | null;
      if (!el?.matches('textarea, input:not([type=checkbox], [type=radio], [type=range], [type=button])')) return;
      if (performance.now() - lastUser > USER_FOCUS_MS) return;
      const flying = aside
        .getAnimations()
        .some((a) => (a as CSSTransition).transitionProperty === 'transform' && a.playState === 'running');
      if (!flying) {
        setGrown(true);
        return;
      }
      stop?.();
      stop = afterSlide(aside, () => setGrown(true));
    };
    aside.addEventListener('focusin', onFocus);
    aside.addEventListener('pointerdown', onUser, true);
    aside.addEventListener('touchstart', onUser, { capture: true, passive: true });
    aside.addEventListener('keydown', onUser, true);
    return () => {
      stop?.();
      aside.removeEventListener('focusin', onFocus);
      aside.removeEventListener('pointerdown', onUser, true);
      aside.removeEventListener('touchstart', onUser, true);
      aside.removeEventListener('keydown', onUser, true);
    };
  }, [detent, bodyOn]);
  // СМЕНА ВЫСОТЫ ЛИСТА — ХОДОМ, А НЕ СКАЧКОМ (ТЗ ANIM-FIX О1.1, 04.10.2026; ночь 03→04.10:
  // «Определённое сообщение», «Содержит слово», «Сообщение в директ» — лист «встаёт за 1 кадр,
  // путь 400 px, анимаций 0»). Меню видов мастера — полулист, страница вида открывается ТЕМ ЖЕ
  // уровнем (winKey, подмена на месте) уже полным листом; так же полулист растёт под кареткой
  // (grown). Верх листа — свойство раскладки (`top`), его переход не анимируется, и лист
  // прыгал. Приём FLIP: прежний верх запомнен, после смены лист ставится трансформом туда,
  // где был, и едет на место ролью листа (transform var(--m-dur) var(--m-ease), cabinet.css),
  // как родной лист iOS при смене детента. Только телефон: на широком экране детента нет.
  const shortNow = !!(p.sheet && p.sheetDetent && !grown);
  const topKey = `${p.sheet ? 1 : 0}|${shortNow ? 1 : 0}|${sheetTopFromDetent(p.sheetDetent) ?? ''}`;
  const placedTop = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = asideRef.current;
    if (!el) return;
    // offsetTop у fixed-слоя — его верх от окна БЕЗ трансформа: ход открытия не мешает.
    const now = el.offsetTop;
    const was = placedTop.current;
    placedTop.current = now;
    if (was === null || Math.abs(was - now) < 1 || !on || !p.sheet) return;
    if (!phoneLike(el) || document.documentElement.classList.contains('app')) return;
    el.style.transition = 'none';
    el.style.transform = `translateY(${was - now}px)`;
    void el.offsetWidth;
    el.style.transition = '';
    el.style.transform = '';
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ход — только на смену верха листа
  }, [topKey]);
  useEffect(() => {
    const keep = (): void => {
      if (asideRef.current) placedTop.current = asideRef.current.offsetTop;
    };
    window.addEventListener('resize', keep);
    return () => window.removeEventListener('resize', keep);
  }, []);
  // «ШАГ N ИЗ M» ГАСНЕТ ПРИ ПРОКРУТКЕ ЛИСТА (паритет с ScrStyle.Wiz.progFade приложения,
  // владелец 09.09.2026: строка хода уезжает вверх и исчезает на первых 24 пунктах,
  // кружки остаются). Доля хода уходит переменной --dr-p на сам лист, рисует cabinet.css.
  useEffect(() => {
    const body = bodyRef.current;
    const aside = asideRef.current;
    if (!body || !aside) return;
    let raf = 0;
    const apply = () => {
      raf = 0;
      aside.style.setProperty('--dr-p', Math.min(1, Math.max(0, body.scrollTop) / 24).toFixed(3));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(apply);
    };
    apply();
    body.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      body.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [p]);

  useSheetKeyboardProxy(asideRef, top && open);
  // Свайп-вниз — у листов; свайп слева направо — у горизонтальных окон конструктора
  // (владелец 13.08.2026). Оба ведут «назад на уровень»: у окна с одним уровнем это
  // прежнее закрытие, у вложенного — возврат к нижнему.
  // ЛИСТЫ ТЕПЕРЬ ТОЖЕ ХОДЯТ СТОПКОЙ (владелец 25.08.2026, шторка ленты): свайп-вниз на
  // ВЛОЖЕННОМ листе снимает только его, родительский остаётся живым под ним. У корневого
  // листа (все прочие шторки кабинета) — ровно прежнее закрытие целиком.
  // Корневой лист ОКНА КОНСТРУКТОРА со своей «назад» (winKey + onBack) свайпом уходит НА
  // УРОВЕНЬ ВЫШЕ, а не закрывается: ровно так вёл себя свайп у горизонтального окна до
  // 25.08.2026 (useSideDrag → onBackLevel). У прочих листов кабинета — прежнее закрытие.
  // Закрытие РУКОЙ (крестик, «‹» вложенного уровня, смахивание) — через вопрос уровня, если
  // он его задал (closeAsk): мастер создания поверх каталога снимается «‹», и это тоже отмена.
  const closeAsk = p.closeAsk;
  // Ответ — ушёл ли уровень: true сразу (вопроса нет) или обещание ответа на вопрос. Его ждёт
  // свайп-назад (useSideDrag): отказ «закрыть?» возвращает слой на место, согласие — нет (М66).
  const guarded = useCallback(
    (go: () => void) => (): boolean | Promise<boolean> => {
      if (!closeAsk) {
        go();
        return true;
      }
      return closeAsk().then((ok) => {
        if (ok) go();
        return ok;
      });
    },
    [closeAsk],
  );
  const askedClose = useMemo(() => guarded(onClose), [guarded, onClose]);
  const askedBack = useMemo(() => guarded(onBackLevel), [guarded, onBackLevel]);
  // Своё действие кружка (onLead) — вместо штатного у этого уровня; см. DrawerProps.onLead.
  const onLead = p.onLead;
  const askedLead = useMemo(() => (onLead ? guarded(onLead) : null), [guarded, onLead]);
  // Вид жеста — по виду слоя (lib/drawer-levels, levelDrag): страница внутри листа — вправо.
  const drag = levelDrag(p, nested);
  useSheetDrag(
    asideRef,
    !!(top && open && drag === 'down'),
    askedLead ?? (nested || (p.winKey && p.onBack) ? askedBack : askedClose),
  );
  // Свайп-назад — у ЛЮБОГО горизонтального слоя (не листа): на телефоне все они едут справа
  // (подстраницы «Аккаунта», окна бота, пикеры), а не только окна с флагом `side` (ТЗ ANIM-FIX
  // О5 П3: «Уведомления», «Справка» свайпом не закрывались вовсе).
  useSideDrag(asideRef, !!(top && open && drag === 'side'), askedBack);

  // «Назад» вместо крестика: вложенный уровень стека ИЛИ свой onBack (подмена содержимого
  // на десктопе и в старом флоу пикеров). Закрытие всего окна — только с корневого уровня.
  const lead = askedLead ?? (nested ? askedBack : (p.onBack ?? askedClose));
  // «‹» У ЛЮБОГО ВЛОЖЕННОГО УРОВНЯ, «×» ТОЛЬКО У КОРНЯ (ТЗ WEB-CAUSES М83, решение владельца В1
  // 06.10.2026; кадры iOS K12/C04/R07). Прежнее исключение «лист-стопка поверх листа (`sheet` +
  // `deck`) — крестиком» (Н39/П130) снято: карточка подключения, страница папки, окно рассылки из «⋯»
  // показывали «×», хотя снимают один уровень. «‹» снимает ровно один уровень (lead = askedBack);
  // `onBack` корня — прежний особый случай (подмена содержимого).
  const leadBack = p.lead ? p.lead === 'back' : !!p.onBack || nested;

  return (
    <aside
      ref={setAside}
      className={`drawer${p.wide ? ' wide' : ''}${p.md ? ' md' : ''}${p.full ? ' full' : ''}${p.sheet ? ' sheet' : ''}${p.headless ? ' dr-headless' : ''}${p.subStack ? ' dr-substack' : ''}${p.sheet && p.sheetDetent && !grown ? ' short' : ''}${p.side ? ' dr-side' : ''}${p.cp ? ' cp' : ''}${nested && p.sheet && p.inSheet ? ' dr-insheet' : ''}${nested ? ` dr-lvl${on ? ' dr-lvl-on' : ''}` : ''}`}
      data-lvl={lvlKey}
      /* ВИД СЛОЯ — ОДНИМ СЛОВОМ СЛОВАРЯ (NATIVE-SLIDE §11.0; APP-CLAIMS-1020 ред. 11, Д10 п.10.12): side — боковой
         («горизонтальная шторка»), deck — лист стопкой снизу, half — полушторка (лист с детентом; после разворота
         атрибут не меняется — вид открытия). По нему экранные тесты сверяют слой с видом того же экрана в приложении. */
      data-layer-kind={p.sheet ? (p.sheetDetent ? 'half' : 'deck') : 'side'}
      /* Уровень-надстройка едет КЛАССОМ (.dr-lvl/.dr-lvl-on), а не inline-трансформом:
         на широком экране закрытая точка не «translateX(100%)», а на отступ панели
         дальше — иначе за правым краем торчала бы её полоска. Числа живут в cabinet.css
         рядом с геометрией шторки, одним местом на оба размера экрана. */
      style={grown ? undefined : sheetVar(p)}
      role="dialog"
      aria-modal="true"
      aria-label={p.title}
    >
      {/* Шапка у ВСЕХ окон ОДНА (владелец 2026-08-11: «модалка обязана быть как на
          других страницах — слева крестик, справа акцентная кнопка»): особой
          cp-шапки ЧП больше нет, окна конструктора носят ту же dr-head, что
          «О сценарии», мастер шаблона и «Модели». */}
      <div className="dr-head">
        {p.headerLeft ? (
          p.headerLeft
        ) : leadBack ? (
          /* «Назад» (вложенный уровень) — ОСТРАЯ СКОБКА ‹, как в iOS. ЗАКРЫТИЕ — всегда
             крестик (владелец 2026-08-11: «сверху слева крестик, справа галочка»);
             прежняя скобка у горизонтальных модалок при close врала — это не «назад». */
          <KBareButton type="button" className="dr-close" onClick={lead} aria-label={t('back')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M14.5 5.5 8 12l6.5 6.5" />
            </svg>
          </KBareButton>
        ) : (
          <KBareButton type="button" className="dr-close" onClick={lead} aria-label={t('close')}>
            {/* Тонкий рисованный крестик (референс — шторки ChatGPT/iOS), а не тяжёлый
                текстовый ✕; та же обводка, что у скобки «назад» выше. */}
            <svg className="dr-glyph-x" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
            {/* ГОРИЗОНТАЛЬНЫЙ СЛОЙ НА ТЕЛЕФОНЕ — СТРАНИЦА СТОПКИ, И ЗАКРЫВАЕТСЯ «НАЗАД»
                (паритет веб = приложение, владелец 09.09.2026): в приложении такой слой —
                push в UINavigationController со скобкой «‹» (InboxScreen, ChatViewController,
                страницы «Аккаунта»). Какой глиф показать, решает CSS по виду слоя и ширине:
                вертикальный лист и десктоп — крестик, горизонтальный слой на телефоне — скобка. */}
            <svg className="dr-glyph-back" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M14.5 5.5 8 12l6.5 6.5" />
            </svg>
          </KBareButton>
        )}
        {p.subtitle ? (
          /* Серая строка под заголовком (счётчик шагов мастера, владелец 2026-08-05). */
          <div className="dr-head-t">
            <h3>{p.title}</h3>
            <p>
              {typeof p.progress === 'number' ? (
                // Кольцо рисуется CSS по --p (conic-gradient) — своих цветов нет, роли палитры.
                <span className="dr-ring" style={{ '--p': Math.min(1, Math.max(0, p.progress)) } as CSSProperties} aria-hidden />
              ) : null}
              {p.subtitle}
            </p>
          </div>
        ) : (
          <h3 className={p.largeTitle ? 'dr-h3--large' : undefined}>{p.title}</h3>
        )}
        {/* Корзины в шапке НЕТ (владелец 2026-08-11: «слева крестик, справа
            галочка» — и всё): удаление шага/заметки живёт на тулбаре выделенной
            карточки холста/ленты (К9). onTrash остаётся ради нативного верха
            приложения (native-layer-host). */}
        {p.headerRight ? (
          p.headerRight
        ) : p.actionLabel ? (
          <KBareButton
            type="button"
            className="dr-act"
            title={p.actionLabel}
            aria-label={p.actionLabel}
            disabled={p.actionDisabled === true}
            onClick={() => p.onAction?.()}
          >
            {/* Канонная галочка — тот же SVG-росчерк, что у выбранного в списках
                (kf-ch__tick); текстовый символ ✓ рисовался другим (владелец 11.08). */}
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.3} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M4.5 12.5l5 5 10-11" />
            </svg>
          </KBareButton>
        ) : p.actionIcon === 'line.3.horizontal.decrease' && p.onAction ? (
          /* Кнопка-фильтр В СТРОКЕ ШАПКИ (владелец 11.08.2026: кружок в теле слоя
             болтался под шапкой с дырой). В приложении её рисует натив по actionIcon;
             веб-шторка понимает пока только этот значок. */
          <KBareButton
            type="button"
            className="dr-act wiz-sec"
            aria-label={p.title}
            onClick={() => p.onAction?.()}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden>
              <path d="M4 7h16M7 12h10M10 17h4" />
            </svg>
          </KBareButton>
        ) : (
          <span className="dr-act hidden" aria-hidden="true" />
        )}
      </div>
      {/* ЗАКОН 3: содержимое слоя всегда рисуется по-телефонному — и на телефоне, и в
          панели-стопке на широком экране (usePhoneLook; CSS — @container cab). */}
      <div className="dr-body" ref={bodyRef}>
        {/* Крупный заголовок списка (largeTitle, п.10.6) — первой строкой тела, уезжает с прокруткой. */}
        {p.largeTitle ? <h1 className="dr-large">{p.title}</h1> : null}
        <PhoneLookCtx.Provider value={true}>{bodyOn ? p.body : null}</PhoneLookCtx.Provider>
      </div>
    </aside>
  );
}

// ── Закрытие iOS-шторки жестом вниз (владелец 2026-07-18) — по канону Apple
// «Designing Fluid Interfaces» (WWDC18): палец ведёт шторку 1:1 (только когда её
// контент прокручен к верху, как в iOS); на отпускании импульс проецируется
// экспоненциальным затуханием UIScrollView (projected = dy + v·decel/(1−decel),
// decel 0.998 ⇒ v·499) — закрываем, если спроецированная точка пересекает ПОЛОВИНУ
// высоты шторки: слабый свайп пружинит назад, сильный флик закрывает отовсюду.
// Вверх — резинка ×0.15. Только мобила и только sheet-модалки. Общий хук обоих
// слоёв drawer (слой-2 — «Информация» поверх формы модели).
function useSheetDrag(
  ref: React.RefObject<HTMLElement | null>,
  enabled: boolean,
  onDismiss: () => void,
) {
  useEffect(() => {
    const aside = ref.current;
    if (!enabled || !aside || !('ontouchstart' in window)) return;
    if (!phoneLike(aside)) return;
    const body = aside.querySelector('.dr-body');
    let phase: 'idle' | 'maybe' | 'drag' = 'idle';
    let startX = 0;
    let startY = 0;
    let dy = 0;
    let lastY = 0;
    let lastT = 0;
    let v = 0;
    // КОЛОДА ИДЁТ ЗА ПАЛЬЦЕМ (претензия владельца 01.10.2026, видео: «дёргается анимация»):
    // корневой лист тянут вниз — экран под ним возвращается на место НА ТУ ЖЕ ДОЛЮ, а на
    // отпускании уходит ОДНИМ движением с листом (тот же ход --m-dur/--m-ease). Раньше колода
    // узнавала о закрытии только из onDismiss через 240 мс — лист уже уехал, а экран стоял
    // уменьшенным и возвращался вторым рывком. Вложенный лист (.dr-lvl) колоду не трогает:
    // под ним остаётся родительский лист. Вид — cabinet.css (.deck-drag / .deck-away).
    // Экран под листом ищем В МОМЕНТ жеста: класс .has-sheet встаёт позже этого эффекта.
    let cab: HTMLElement | null = null;
    const deckP = (p: number | null) => {
      if (!cab) return;
      if (p === null) cab.style.removeProperty('--deck-p');
      else cab.style.setProperty('--deck-p', Math.min(1, Math.max(0, p)).toFixed(4));
    };

    const onStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || e.touches.length > 1) return;
      startX = t.clientX;
      startY = lastY = t.clientY;
      dy = 0;
      v = 0;
      lastT = performance.now();
      phase = 'maybe';
    };
    const onMove = (e: TouchEvent) => {
      if (phase === 'idle') return;
      const t = e.touches[0];
      if (!t) return;
      const ddy = t.clientY - startY;
      const ddx = t.clientX - startX;
      if (phase === 'maybe') {
        if (Math.abs(ddy) < 8 && Math.abs(ddx) < 8) return;
        const scrolled = (body?.scrollTop ?? 0) > 0;
        if (ddy <= 0 || Math.abs(ddy) < Math.abs(ddx) * 1.2 || scrolled || !e.cancelable) {
          phase = 'idle';
          return;
        }
        phase = 'drag';
        aside.style.transition = 'none';
        cab = aside.classList.contains('dr-lvl') ? null : aside.closest<HTMLElement>('.cab.has-sheet');
        cab?.classList.remove('deck-away');
        cab?.classList.add('deck-drag');
      }
      if (!e.cancelable) return;
      e.preventDefault();
      dy = ddy;
      const now = performance.now();
      if (now - lastT > 0) v = (t.clientY - lastY) / (now - lastT);
      lastY = t.clientY;
      lastT = now;
      aside.style.transform = `translateY(${dy >= 0 ? dy : dy * 0.15}px)`;
      deckP(dy / Math.max(1, aside.getBoundingClientRect().height));
    };
    const finish = () => {
      if (phase !== 'drag') {
        phase = 'idle';
        return;
      }
      phase = 'idle';
      const h = aside.getBoundingClientRect().height;
      const projected = dy + v * 499;
      // Отпускание — тот же ход, что открытие и закрытие крестиком: роль --m-dur по --m-ease
      // (ТЗ WEB-CAUSES П6; прежние 0.32 с своей кривой давали третий ход колоды и листа).
      aside.style.transition = 'transform var(--m-dur) var(--m-ease)';
      if (projected > h / 2) {
        aside.style.transform = 'translateY(100%)';
        // Колода уходит ВМЕСТЕ с листом: класс ставит ей закрытый вид с тем же ходом, что у
        // листа; когда onDismiss снимет .has-sheet, вид уже тот же — второго движения нет.
        cab?.classList.remove('deck-drag');
        cab?.classList.add('deck-away');
        window.setTimeout(() => {
          onDismiss();
          window.setTimeout(() => {
            aside.style.transition = '';
            aside.style.transform = '';
          }, 60);
        }, 240);
        window.setTimeout(() => {
          cab?.classList.remove('deck-away');
          deckP(null);
        }, 440);
      } else {
        aside.style.transform = 'translateY(0)';
        // Не дотянули — экран под листом возвращается назад тем же ходом, что лист.
        cab?.classList.remove('deck-drag');
        cab?.classList.add('deck-back');
        deckP(0);
        window.setTimeout(() => {
          aside.style.transition = '';
          aside.style.transform = '';
          cab?.classList.remove('deck-back');
          deckP(null);
        }, 380);
      }
    };

    aside.addEventListener('touchstart', onStart, { passive: true });
    aside.addEventListener('touchmove', onMove, { passive: false });
    aside.addEventListener('touchend', finish, { passive: true });
    aside.addEventListener('touchcancel', finish, { passive: true });
    return () => {
      aside.removeEventListener('touchstart', onStart);
      aside.removeEventListener('touchmove', onMove);
      aside.removeEventListener('touchend', finish);
      aside.removeEventListener('touchcancel', finish);
      aside.style.transition = '';
      aside.style.transform = '';
      cab?.classList.remove('deck-drag', 'deck-back');
    };
  }, [ref, enabled, onDismiss]);
}

// ── СВАЙП-НАЗАД БОКОВОГО СЛОЯ — ИЗ ЛЮБОГО МЕСТА, КАК В TELEGRAM iOS (ТЗ ANIM-FIX О5 П3, претензия
// владельца 04.10.2026, видео). Прежде жест стартовал только в 40 px у левой кромки — а там его
// перехватывает Safari (свой «назад» по истории, уезжает вся страница), и слой пальцем не шёл
// вовсе. Теперь горизонтальный свайп вправо из любого места слоя ведёт слой пальцем, экран
// (или слой) под ним идёт за пальцем той же долей — параллакс --m-parallax и плёнка --m-dim
// сходят пропорционально, — а на отпускании слой уходит (дальше 1/3 ширины или бросок вправо)
// либо возвращается, ходом --m-dur/--m-ease. Уход — «назад на уровень» (у корня — закрытие;
// уровень с адресом снимает popstate тем же ходом, leaveLevel).
// Распознавание: люфт 8 px, горизонталь побеждает при |dx| > |dy|·1.2, иначе жест отдаётся
// прокрутке. Не стартует: у самой кромки экрана (зона жеста Safari — его не трогаем) и внутри
// горизонтального ряда (полосы папок, чипы, карусели), который сам прокрутится вправо.
const SIDE_SAFARI_EDGE = 20; // px от левой кромки экрана: зона системного «назад» Safari
const SIDE_SLOP = 8; // люфт до решения «чей жест»
const SIDE_FLICK = 0.5; // px/мс: бросок вправо закрывает и с малого хода
const SIDE_MOVE = 'transform var(--m-dur) var(--m-ease)';

/** Внутри горизонтального ряда (до самого слоя), который сам прокрутится от пальца вправо
 *  (есть что показать слева), — жест отдаётся ряду. Ряд у левого края или с переполнением на
 *  пару пикселей (округление раскладки) жест не держит — слой уходит, как в Telegram iOS. */
function rowTakes(from: EventTarget | null, stop: HTMLElement): boolean {
  let el = from instanceof Element ? from : null;
  while (el && el !== stop) {
    if (el instanceof HTMLInputElement && el.type === 'range') return true;
    if (el instanceof HTMLElement && el.scrollWidth - el.clientWidth > SIDE_SLOP && el.scrollLeft > 1) {
      const ox = getComputedStyle(el).overflowX;
      if (ox === 'auto' || ox === 'scroll') return true;
    }
    el = el.parentElement;
  }
  return false;
}

/** Что под слоем: корень — экран .cab (если под ним параллакс .has-side), вложенный — живой
 *  уровень ниже в той же подложке. */
function sideUnderOf(aside: HTMLElement): { cab: HTMLElement | null; lower: HTMLElement | null } {
  if (!aside.classList.contains('dr-lvl')) {
    const cab = aside.closest<HTMLElement>('.cab');
    return { cab: cab?.classList.contains('has-side') ? cab : null, lower: null };
  }
  let el = aside.previousElementSibling;
  while (el) {
    if (el.classList.contains('drawer') && (!el.classList.contains('dr-lvl') || el.classList.contains('dr-lvl-on'))) {
      return { cab: null, lower: el as HTMLElement };
    }
    el = el.previousElementSibling;
  }
  return { cab: null, lower: null };
}

function useSideDrag(
  ref: React.RefObject<HTMLElement | null>,
  enabled: boolean,
  onDismiss: () => void | boolean | Promise<boolean>,
) {
  useEffect(() => {
    const aside = ref.current;
    if (!enabled || !aside || !('ontouchstart' in window)) return;
    if (!phoneLike(aside)) return; // корень-панель на широком не трогаем; уровни внутри неё — как телефон (М48)
    let phase: 'idle' | 'maybe' | 'drag' = 'idle';
    let startX = 0;
    let startY = 0;
    let dx = 0;
    let lastX = 0;
    let lastT = 0;
    let v = 0;
    let under: { cab: HTMLElement | null; lower: HTMLElement | null } = { cab: null, lower: null };
    let stopEnd: (() => void) | null = null;
    // СЛОЙ, УШЕДШИЙ СВАЙПОМ, НЕ ВОЗВРАЩАЕТСЯ, ПОКА ХОЗЯИН ЕГО СНИМАЕТ (ТЗ WEB-CAUSES М66, видео
    // владельца 06.10 14:10: «свайпаю вправо… и после этого происходит дёргание»). Уровень с адресом
    // снимается шагом истории: onDismiss → back() → history.go(-1) → popstate → снятие DOM. В Safari на
    // iPhone шаг истории приходит позже конца хода (0,35 с), и прежний settle() по концу хода видел
    // «слой ещё живой» = «вопрос „закрыть?“ отказал» и возвращал слой ходом на место — слой въезжал
    // обратно и через миг уезжал вторым ходом по popstate (замер видео: кадр 34 чисто, кадр 39 снова
    // слой). Теперь после согласия слой стоит за кромкой (inline-transform точки ухода), пока хозяин
    // его не снимет (класс .show / .dr-lvl-on / DOM — MutationObserver), и только тогда следы жеста
    // снимаются без хода. Назад на место — только по ОТКАЗУ вопроса (обещание guarded → false).
    let refused = false;
    let awayPending = false;
    let waitStop: (() => void) | null = null;
    const isGone = (): boolean =>
      !aside.isConnected ||
      (aside.classList.contains('dr-lvl')
        ? !aside.classList.contains('dr-lvl-on')
        : !aside.closest('.drawer-ov')?.classList.contains('show'));
    /** Ждать снятия слоя хозяином; страховка — 2,5 с (дальше — как прежде, settle). */
    const whenGone = (done: () => void): void => {
      const root = aside.closest('.cab') ?? document.body;
      let timer = 0;
      const mo = new MutationObserver(() => {
        if (isGone()) fin();
      });
      function fin(): void {
        waitStop?.();
        done();
      }
      waitStop = () => {
        mo.disconnect();
        window.clearTimeout(timer);
        waitStop = null;
      };
      mo.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
      timer = window.setTimeout(fin, 2500);
    };

    /** Доля ухода слоя 0…1 → экран/слой под ним возвращается на ту же долю. */
    const follow = (p: number): void => {
      if (under.cab) {
        under.cab.classList.add('side-drag');
        under.cab.style.setProperty('--side-p', p.toFixed(4));
      }
      const lo = under.lower;
      if (lo) {
        lo.style.transition = 'none';
        lo.style.filter = `brightness(calc(1 - var(--m-dim) * ${(1 - p).toFixed(4)}))`;
        if (!lo.classList.contains('sheet')) {
          lo.style.transform = `translateX(calc(-1 * var(--m-parallax) * ${(1 - p).toFixed(4)}))`;
        }
      }
    };
    /** Отпустили: под-слой едет ходом слоя в точку `away` (1 — на место, слой ушёл) или
     *  обратно в вид «под слоем» (CSS, inline снимается). */
    const release = (away: boolean): void => {
      const c = under.cab;
      if (c) {
        c.classList.remove('side-drag');
        if (away) c.style.setProperty('--side-p', '1');
        else c.style.removeProperty('--side-p');
      }
      const lo = under.lower;
      if (lo) {
        if (away) {
          lo.style.transition = `${SIDE_MOVE}, filter var(--m-dur) var(--m-ease)`;
          lo.style.filter = 'brightness(1)';
          if (!lo.classList.contains('sheet')) lo.style.transform = 'translateX(0)';
        } else {
          lo.style.transition = '';
          lo.style.filter = '';
          lo.style.transform = '';
        }
      }
    };
    /** Снять следы жеста, когда слой доехал. Ушёл — снимаем без хода (CSS уже стоит там же);
     *  не ушёл (вопрос «закрыть?» отказал) — возвращаем ходом на место. */
    /** Конец хода ухода: снят (или отказ) — снять следы; иначе ждать снятия за кромкой. */
    const afterAway = (): void => {
      if (refused || isGone()) settle();
      else whenGone(settle);
    };
    const settle = (): void => {
      waitStop?.();
      awayPending = false;
      const gone = isGone();
      if (gone) {
        aside.style.transition = 'none';
        aside.style.transform = '';
        void aside.offsetWidth;
        aside.style.transition = '';
        under.cab?.style.removeProperty('--side-p');
        const lo = under.lower;
        if (lo) {
          lo.style.transition = 'none';
          lo.style.filter = '';
          lo.style.transform = '';
          void lo.offsetWidth;
          lo.style.transition = '';
        }
      } else {
        aside.style.transition = SIDE_MOVE;
        aside.style.transform = '';
        release(false);
        stopEnd = afterSlide(aside, () => {
          aside.style.transition = '';
        });
      }
      under = { cab: null, lower: null };
    };

    const onStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || e.touches.length > 1 || phase === 'drag') return;
      if (t.clientX < SIDE_SAFARI_EDGE) return;
      if (rowTakes(e.target, aside)) return;
      startX = lastX = t.clientX;
      startY = t.clientY;
      dx = 0;
      v = 0;
      lastT = performance.now();
      phase = 'maybe';
    };
    const onMove = (e: TouchEvent) => {
      if (phase === 'idle') return;
      const t = e.touches[0];
      if (!t) return;
      const ddx = t.clientX - startX;
      const ddy = t.clientY - startY;
      if (phase === 'maybe') {
        if (Math.abs(ddx) < SIDE_SLOP && Math.abs(ddy) < SIDE_SLOP) return;
        // Свайп взяли папки списка внутри слоя (листают к прошлой папке) — слой уступает.
        if (ddx <= 0 || Math.abs(ddx) <= Math.abs(ddy) * 1.2 || !e.cancelable || takenByFolders(e)) {
          phase = 'idle';
          return;
        }
        phase = 'drag';
        stopEnd?.();
        stopEnd = null;
        under = sideUnderOf(aside);
        aside.style.transition = 'none';
        // Начало хода — от точки, где палец перешёл люфт: слой не прыгает на 8 px.
        startX = t.clientX;
      }
      if (!e.cancelable) return;
      e.preventDefault();
      dx = Math.max(0, t.clientX - startX);
      const now = performance.now();
      if (now - lastT > 0) v = (t.clientX - lastX) / (now - lastT);
      lastX = t.clientX;
      lastT = now;
      aside.style.transform = `translateX(${dx}px)`;
      const w = aside.getBoundingClientRect().width || window.innerWidth;
      follow(Math.min(1, dx / w));
    };
    const finish = () => {
      if (phase !== 'drag') {
        phase = 'idle';
        return;
      }
      phase = 'idle';
      const w = aside.getBoundingClientRect().width || window.innerWidth;
      const away = v > SIDE_FLICK || (v > -SIDE_FLICK && dx > w / 3);
      aside.style.transition = SIDE_MOVE;
      aside.style.transform = away ? 'translateX(100%)' : 'translateX(0)';
      release(away);
      refused = false;
      awayPending = away;
      let slid = false;
      stopEnd = afterSlide(aside, () => {
        stopEnd = null;
        slid = true;
        if (!away) {
          aside.style.transition = '';
          aside.style.transform = '';
        } else afterAway();
      });
      if (!away) return;
      const asked = onDismiss();
      if (asked instanceof Promise) {
        void asked.then((ok) => {
          if (ok) return;
          refused = true;
          // Ход уже кончился (слой ждёт снятия за кромкой) — возвращаем сейчас; иначе вернёт конец хода.
          if (slid) settle();
        });
      }
    };

    aside.addEventListener('touchstart', onStart, { passive: true });
    aside.addEventListener('touchmove', onMove, { passive: false });
    aside.addEventListener('touchend', finish, { passive: true });
    aside.addEventListener('touchcancel', finish, { passive: true });
    return () => {
      aside.removeEventListener('touchstart', onStart);
      aside.removeEventListener('touchmove', onMove);
      aside.removeEventListener('touchend', finish);
      aside.removeEventListener('touchcancel', finish);
      // Слой ушёл свайпом и ждёт снятия хозяином (М66) — ожидание доживёт само и снимет следы.
      if (waitStop) return;
      // Эффект снимается и посреди ухода (слой перестал быть верхним/живым после onDismiss):
      // ход доедет сам, следы снимет страховка по концу хода.
      if (stopEnd) {
        stopEnd();
        stopEnd = afterSlide(aside, awayPending ? afterAway : settle);
        return;
      }
      aside.style.transition = '';
      aside.style.transform = '';
    };
  }, [ref, enabled, onDismiss]);
}

// ── ПЕРВИЧНЫЙ ФОКУС поля, которое приезжает ВМЕСТЕ СО ШТОРКОЙ (О12, видео владельца
// «она неправильно выезжает: контент едет по диагонали»).
//
// INVARIANT(scriptera): поле, смонтированное вместе со шторкой, НЕ забирает фокус на
// узком экране. Причина: React-овский autoFocus зовёт focus() прямо в коммите — то есть
// в жесте тапа, когда шторка ещё стоит за правым краем и её 0.42s выезд не начался. iOS
// на фокус поднимает клавиатуру и НАСИЛЬНО «показывает» поле: сперва ужимает видимый
// вьюпорт, потом тащит его вверх (канон IOS-KEYBOARD-CANON §1.1: scrollY 0 → 337). Это
// движение по вертикали складывается с выездом шторки по горизонтали — человек видит
// диагональ и не понимает, откуда что приехало; попутно замок фона запоминает уже
// уехавшую точку, и на закрытии лента прыгает. Клавиатуру в шторке поднимает ТОЛЬКО
// фокус-прокси на тапе по полю (useSheetKeyboardProxy ниже, канон §6) — программного
// первичного фокуса на мобиле не бывает. Канон: docs/NATIVE-SLIDE-PLAN.md §11
// (приезжающий слой едет единым куском, своего движения внутри у него нет).
//
// На широком экране всё это неприменимо: там окно — плавающая панель без выезда по
// экрану и с железной клавиатурой, фокус в первом поле экономит человеку тап.
export function useSheetAutoFocus<T extends HTMLElement>(): React.RefObject<T | null> {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    if (typeof window === 'undefined' || window.innerWidth <= 820) return;
    const el = ref.current;
    if (!el) return;
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
  }, []);
  return ref;
}

// ── Клавиатура iOS в полях листа — ТОЛЬКО фокусная часть механики чата (канон
// IOS-KEYBOARD-CANON §6, журнал UI-FIXES-JOURNAL): тап по полю перехватываем
// на touchend, первичный фокус — в прокси у ВЕРХА листа (iOS нечего
// «показывать» → вьюпорт не двигает, кнопки шапки не прыгают, и после
// закрытия модалки нечему «зависать поднятым», §1.4), после подъёма
// клавиатуры фокус тихо переносится в настоящее поле. Геометрию листа
// (height/transform) НЕ трогаем — см. урок отката 12:39 ниже.
// Общий хук обоих слоёв drawer (слой-2 — шторка поверх модалки, тоже бывает с полями).
function useSheetKeyboardProxy(ref: React.RefObject<HTMLElement | null>, active: boolean) {
  useEffect(() => {
    const vv = window.visualViewport;
    const aside = ref.current;
    if (!active || !vv || !aside || !('ontouchstart' in window)) return;

    const proxy = document.createElement('input');
    proxy.type = 'text';
    proxy.autocomplete = 'off';
    proxy.tabIndex = -1;
    proxy.setAttribute('aria-hidden', 'true');
    // ≥16px — иначе iOS авто-зумит (журнал дизайн-фиксов, 2026-07-09)
    proxy.style.cssText =
      'position:absolute;top:0;left:8px;width:1px;height:1px;opacity:0;border:0;padding:0;background:transparent;font-size:16px;pointer-events:none;';
    aside.appendChild(proxy);

    let raf = 0;
    let settleT = 0;
    let target: HTMLElement | null = null;
    // Когда наши поля в последний раз теряли фокус: iOS блюрит активное поле уже
    // на touchstart — к touchend «в фокусе никого», и проверка по activeElement
    // одна не спасает (владелец 13:4x: дёргание при перещёлкивании и в Safari,
    // и в PWA). Свежий блюр = перещёлкивание при открытой клавиатуре → натив.
    let lastBlur = 0;

    const transfer = (): void => {
      window.clearTimeout(settleT);
      const el = target;
      target = null;
      if (el && document.activeElement === proxy) {
        try {
          el.focus({ preventScroll: true });
        } catch {
          el.focus();
        }
      }
    };
    // ГЕОМЕТРИЮ ЛИСТА НЕ ТРОГАЕМ (урок отката 12:39, журнал UI-FIXES):
    // кап height/transform, посчитанный «от верха», на мобильном bottom-sheet
    // оставлял от листа полоску над клавиатурой. Механика — только фокусная:
    // прокси у верха листа гасит iOS-толчок, больше ничего не двигаем.
    const apply = (): void => {
      raf = 0;
      if (vv.scale > 1.05) return; // пинч — не вмешиваемся
      if (target) {
        window.clearTimeout(settleT);
        settleT = window.setTimeout(transfer, 160); // тишина событий → перенос фокуса
      }
    };
    const onVV = (): void => {
      if (!raf) raf = requestAnimationFrame(apply);
    };
    const onTouchEnd = (e: TouchEvent): void => {
      const el = e.target as HTMLElement | null;
      if (!el || !/^(INPUT|TEXTAREA)$/.test(el.tagName)) return;
      if ((el as HTMLInputElement).disabled || document.activeElement === el) return;
      // «Клавиатура уже открыта» определяется ПО ФОКУСУ (протокол канона §5.3),
      // НЕ по диффу высот (в устоявшемся состоянии iOS 26 дифф — 0, §1.4).
      // Плюс окно после свежего блюра: iOS блюрит старое поле уже на touchstart,
      // и к touchend activeElement пуст — но это перещёлкивание, не первый тап.
      const a = document.activeElement;
      const editingNow = a === proxy || (a && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName));
      e.preventDefault(); // глушим отложенный нативный фокус (см. канон: touchstart нельзя)
      if (editingNow || Date.now() - lastBlur < 600) {
        // Перещёлкивание при открытой клавиатуре. Нативный перенос фокуса iOS
        // сопровождает «показом» поля — толчок вьюпорта, кнопки листа прыгают
        // (записи 13:38/13:5x, и Safari, и PWA). Поэтому фокус ставим САМИ, в
        // жесте, с preventScroll — клавиатура остаётся, показывать iOS нечего.
        try {
          el.focus({ preventScroll: true });
        } catch {
          el.focus();
        }
        return;
      }
      target = el;
      proxy.focus(); // фокус в жесте → клавиатура придёт, показывать нечего
      window.clearTimeout(settleT);
      settleT = window.setTimeout(transfer, 900); // страховка: события так и не пришли
    };
    const markBlur = (e: FocusEvent): void => {
      const el = e.target as HTMLElement | null;
      if (el === proxy || (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) {
        lastBlur = Date.now();
      }
    };

    vv.addEventListener('resize', onVV);
    vv.addEventListener('scroll', onVV);
    aside.addEventListener('touchend', onTouchEnd);
    aside.addEventListener('focusout', markBlur);
    return () => {
      vv.removeEventListener('resize', onVV);
      vv.removeEventListener('scroll', onVV);
      aside.removeEventListener('touchend', onTouchEnd);
      aside.removeEventListener('focusout', markBlur);
      if (raf) cancelAnimationFrame(raf);
      window.clearTimeout(settleT);
      proxy.remove();
    };
  }, [ref, active]);
}
